// src/time.ts
import { DateTime } from "luxon";
var DEFAULT_TIME_ZONE = "Europe/Berlin";
var DEFAULT_DATETIME_FORMAT = "dd.MM.yyyy HH:mm";
function nowUtc() {
  return /* @__PURE__ */ new Date();
}
function toUtcDateTime(value) {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: "utc" });
  }
  return DateTime.fromISO(value, { zone: "utc" });
}
function formatInZone(value, zone = DEFAULT_TIME_ZONE, format = DEFAULT_DATETIME_FORMAT, locale = "de") {
  const dt = toUtcDateTime(value).setZone(zone);
  if (!dt.isValid) return "";
  return dt.setLocale(locale).toFormat(format);
}
function toIsoUtc(value) {
  const dt = toUtcDateTime(value);
  return dt.isValid ? dt.toUTC().toISO() ?? "" : "";
}
function addSeconds(value, seconds) {
  return new Date(value.getTime() + seconds * 1e3);
}
function isExpired(expiresAt, reference = nowUtc()) {
  const dt = toUtcDateTime(expiresAt);
  return dt.isValid ? dt.toMillis() <= reference.getTime() : true;
}

// src/roles.ts
var ROLES = ["superadmin", "admin", "user"];
var RANK = { user: 1, admin: 2, superadmin: 3 };
function roleAtLeast(role, minimum) {
  return RANK[role] >= RANK[minimum];
}
function canAssignRole(actor, targetRole) {
  if (actor === "superadmin") return true;
  if (actor === "admin") return targetRole === "user" || targetRole === "admin";
  return false;
}
function canManageUser(actor, subjectRole) {
  if (actor === "superadmin") return true;
  if (actor === "admin") return subjectRole === "user" || subjectRole === "admin";
  return false;
}
var SHARE_PERMISSIONS = ["view", "edit"];
var ENVIRONMENT_TYPES = ["cloud", "on_prem", "neutral"];
var USER_GROUP_ROLES = ["member", "manager"];
function canManageGroupMembers(actorRole, isManagerOfGroup) {
  if (actorRole === "admin" || actorRole === "superadmin") return true;
  return isManagerOfGroup;
}

// src/schemas.ts
import { z } from "zod";
var passwordSchema = z.string().min(12, "Das Passwort muss mindestens 12 Zeichen lang sein.").max(128, "Das Passwort darf h\xF6chstens 128 Zeichen lang sein.").regex(/[a-z]/, "Das Passwort muss einen Kleinbuchstaben enthalten.").regex(/[A-Z]/, "Das Passwort muss einen Gro\xDFbuchstaben enthalten.").regex(/[0-9]/, "Das Passwort muss eine Ziffer enthalten.");
var emailSchema = z.string().trim().toLowerCase().email("Bitte eine g\xFCltige E-Mail-Adresse angeben.").max(254);
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Bitte das Passwort eingeben.").max(128)
});
var createUserSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(1, "Bitte einen Anzeigenamen angeben.").max(120),
  role: z.enum(ROLES).default("user")
});
var updateUserSchema = z.object({
  displayName: z.string().trim().min(1, "Bitte einen Anzeigenamen angeben.").max(120).optional(),
  email: emailSchema.optional()
}).refine((value) => value.displayName !== void 0 || value.email !== void 0, {
  message: "Mindestens ein Feld muss angegeben werden."
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Bitte das aktuelle Passwort eingeben.").max(128),
  newPassword: passwordSchema
});

// src/drawing-schemas.ts
import { z as z2 } from "zod";
var drawingNameSchema = z2.string().trim().min(1, "Bitte einen Namen angeben.").max(200);
var createDrawingSchema = z2.object({
  name: drawingNameSchema,
  description: z2.string().trim().max(2e3).optional(),
  groupId: z2.string().uuid().nullable().optional()
});
var updateDrawingSchema = z2.object({
  name: drawingNameSchema.optional(),
  description: z2.string().trim().max(2e3).nullable().optional(),
  groupId: z2.string().uuid().nullable().optional()
}).refine((value) => Object.keys(value).length > 0, { message: "Keine \xC4nderungen angegeben." });
var listDrawingsQuerySchema = z2.object({
  search: z2.string().trim().max(200).optional(),
  groupId: z2.string().uuid().optional(),
  // Query-Param kommt als String an – "true"/"false" explizit mappen
  // (z.coerce.boolean würde auch "false" zu true machen).
  includeSubgroups: z2.enum(["true", "false"]).default("false").transform((value) => value === "true"),
  scope: z2.enum(["all", "own", "shared"]).default("all"),
  sort: z2.enum(["updated", "created", "name"]).default("updated"),
  limit: z2.coerce.number().int().min(1).max(100).default(50),
  offset: z2.coerce.number().int().min(0).default(0)
});
var groupNameSchema = z2.string().trim().min(1, "Bitte einen Namen angeben.").max(120);
var createGroupSchema = z2.object({
  name: groupNameSchema,
  parentId: z2.string().uuid().nullable().optional()
});
var updateGroupSchema = z2.object({
  name: groupNameSchema.optional(),
  parentId: z2.string().uuid().nullable().optional()
}).refine((value) => Object.keys(value).length > 0, { message: "Keine \xC4nderungen angegeben." });

// src/scene-geometry.ts
function snapToGrid(value, grid = 10) {
  return Math.round(value / grid) * grid;
}
function nodeCenter(box) {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}
function resolveEndpoint(endpoint, nodes) {
  if ("nodeId" in endpoint) {
    const node = nodes.get(endpoint.nodeId);
    return node ? nodeCenter(node) : null;
  }
  return { x: endpoint.x, y: endpoint.y };
}
function edgeAnchor(box, target, options = {}) {
  const c = nodeCenter(box);
  const halfW = box.width / 2;
  const halfH = box.height / 2;
  switch (options.port) {
    case "top":
      return { x: c.x, y: box.y };
    case "right":
      return { x: box.x + box.width, y: c.y };
    case "bottom":
      return { x: c.x, y: box.y + box.height };
    case "left":
      return { x: box.x, y: c.y };
    default:
      break;
  }
  const dx = target.x - c.x;
  const dy = target.y - c.y;
  if (dx === 0 && dy === 0) return c;
  if (options.shape === "ellipse") {
    const t = 1 / Math.sqrt((dx / halfW) ** 2 + (dy / halfH) ** 2);
    return { x: c.x + dx * t, y: c.y + dy * t };
  }
  const scaleX = dx === 0 ? Infinity : halfW / Math.abs(dx);
  const scaleY = dy === 0 ? Infinity : halfH / Math.abs(dy);
  const scale = Math.min(scaleX, scaleY);
  return { x: c.x + dx * scale, y: c.y + dy * scale };
}
function orthogonalRoute(from, to, bendRatio = 0.5) {
  const ratio = Math.min(1, Math.max(0, bendRatio));
  if (Math.abs(to.x - from.x) >= Math.abs(to.y - from.y)) {
    const midX = from.x + (to.x - from.x) * ratio;
    return [from, { x: midX, y: from.y }, { x: midX, y: to.y }, to];
  }
  const midY = from.y + (to.y - from.y) * ratio;
  return [from, { x: from.x, y: midY }, { x: to.x, y: midY }, to];
}
function axisLines(start, size) {
  return [start, start + size / 2, start + size];
}
function computeSnap(moving, others, threshold = 6) {
  const result = { x: moving.x, y: moving.y, guides: [] };
  const bestMatch = (getStart, getSize) => {
    const movingLines = axisLines(getStart(moving), getSize(moving));
    let best = null;
    for (const other of others) {
      const otherLines = axisLines(getStart(other), getSize(other));
      for (const ml of movingLines) {
        for (const ol of otherLines) {
          const diff = ol - ml;
          if (Math.abs(diff) <= threshold && (!best || Math.abs(diff) < Math.abs(best.delta))) {
            best = { delta: diff, position: ol };
          }
        }
      }
    }
    return best;
  };
  const xSnap = bestMatch(
    (b) => b.x,
    (b) => b.width
  );
  if (xSnap) {
    result.x = moving.x + xSnap.delta;
    result.guides.push({ axis: "x", position: xSnap.position });
  }
  const ySnap = bestMatch(
    (b) => b.y,
    (b) => b.height
  );
  if (ySnap) {
    result.y = moving.y + ySnap.delta;
    result.guides.push({ axis: "y", position: ySnap.position });
  }
  return result;
}
function inscribedRect(width, height) {
  const w = width / Math.SQRT2;
  const h = height / Math.SQRT2;
  return { x: (width - w) / 2, y: (height - h) / 2, width: w, height: h };
}
function growingCanvasBounds(nodes, opts) {
  let minX = 0;
  let minY = 0;
  let maxX = opts.minWidth;
  let maxY = opts.minHeight;
  for (const n of nodes) {
    if (n.x < 0) minX = Math.min(minX, n.x - opts.padding);
    if (n.y < 0) minY = Math.min(minY, n.y - opts.padding);
    if (n.x + n.width > opts.minWidth) maxX = Math.max(maxX, n.x + n.width + opts.padding);
    if (n.y + n.height > opts.minHeight) maxY = Math.max(maxY, n.y + n.height + opts.padding);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
var ARROW_GAP = 2;
function retractArrowEnds(route, opts) {
  const result = route.map((p) => ({ x: p.x, y: p.y }));
  if (result.length < 2) return result;
  const pull = (endIdx, towardIdx) => {
    const end = result[endIdx];
    const toward = result[towardIdx];
    const dx = toward.x - end.x;
    const dy = toward.y - end.y;
    const len = Math.hypot(dx, dy);
    if (len === 0) return;
    const d = Math.min(opts.gap, len);
    result[endIdx] = { x: end.x + dx / len * d, y: end.y + dy / len * d };
  };
  if (opts.end) pull(result.length - 1, result.length - 2);
  if (opts.start) pull(0, 1);
  return result;
}

// src/scene.ts
import { z as z11 } from "zod";

// src/vlan.ts
import { z as z3 } from "zod";
var vlanDefSchema = z3.object({
  id: z3.string().max(64),
  // 0..4094 (0 und 4095 sind reserviert); ganzzahlig.
  vlanId: z3.number().int().min(0).max(4094),
  name: z3.string().max(64).default(""),
  // Hex-Farbe o. Ae.; nur Laengengrenze, keine Format-Pruefung.
  color: z3.string().max(32),
  // Optionaler IPv4-Adressbereich in CIDR-Notation (z. B. "10.0.10.0/24").
  // Schema prueft nur die Laenge; das Format pruefen UI/Helfer (isValidIpv4Cidr).
  subnet: z3.string().max(32).optional()
});
function findVlan(vlans, id) {
  if (!vlans || !id) return void 0;
  return vlans.find((v) => v.id === id);
}
function vlanColor(vlans, id, fallback) {
  return findVlan(vlans, id)?.color ?? fallback;
}
function duplicateVlanIds(vlans) {
  const seen = /* @__PURE__ */ new Map();
  for (const v of vlans) seen.set(v.vlanId, (seen.get(v.vlanId) ?? 0) + 1);
  return [...seen.entries()].filter(([, count]) => count > 1).map(([vlanId]) => vlanId);
}
function isValidIpv4Cidr(value) {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(value);
  if (!m) return false;
  for (const octet of [m[1], m[2], m[3], m[4]]) {
    if (octet.length > 1 && octet[0] === "0") return false;
    if (Number(octet) > 255) return false;
  }
  const prefix = m[5];
  if (prefix.length > 1 && prefix[0] === "0") return false;
  return Number(prefix) <= 32;
}

// src/user-directory.ts
import { z as z4 } from "zod";
var userDefSchema = z4.object({
  id: z4.string().max(64),
  displayName: z4.string().max(128),
  // UserPrincipalName / Login (E-Mail).
  upn: z4.string().max(128).optional(),
  department: z4.string().max(128).optional(),
  jobTitle: z4.string().max(128).optional(),
  // AccountEnabled; undefined = unbekannt.
  enabled: z4.boolean().optional()
});
function findUser(users, id) {
  if (!users || !id) return void 0;
  return users.find((u) => u.id === id);
}
function userLabel(user) {
  return user.department ? `${user.displayName} \xB7 ${user.department}` : user.displayName;
}

// src/device.ts
import { z as z6 } from "zod";

// src/service-port.ts
import { z as z5 } from "zod";
var PORT_PROTOCOLS = ["tcp", "udp", "both"];
var servicePortSchema = z5.object({
  port: z5.number().int().min(0).max(65535),
  protocol: z5.enum(PORT_PROTOCOLS).optional(),
  service: z5.string().max(64).optional()
});
function isValidPortNumber(value) {
  return Number.isInteger(value) && value >= 1 && value <= 65535;
}
function formatServicePort(sp) {
  if (!sp.protocol) return String(sp.port);
  const suffix = sp.protocol === "both" ? "TCP/UDP" : sp.protocol.toUpperCase();
  return `${sp.port}(${suffix})`;
}

// src/device.ts
var DEVICE_KINDS = [
  "pc",
  "laptop",
  "printer",
  "scanner",
  "phone",
  // Handy
  "accesspoint",
  "camera",
  "server",
  "pbx",
  // Telefonanlage
  "deskphone",
  // Telefon
  "other"
];
var IP_MODES = ["static", "dhcp", "dhcp-fixed"];
var deviceDataSchema = z6.object({
  kind: z6.enum(DEVICE_KINDS),
  // Referenz auf ein VLAN (VlanDef.id) im zentralen Register.
  vlanRef: z6.string().max(64).optional(),
  ipMode: z6.enum(IP_MODES).optional(),
  ipValue: z6.string().max(64).optional(),
  // DNS-Name/Hostname, unter dem das Geraet erreichbar ist.
  dns: z6.string().max(255).optional(),
  // Benutzer/Verantwortlicher; nur bei bestimmten Typen sinnvoll (siehe DEVICE_FIELDS).
  user: z6.string().max(128).optional(),
  // Referenz auf einen Verzeichnis-Eintrag (UserDef.id); device.user bleibt Freitext-Rueckfall.
  userRef: z6.string().max(64).optional(),
  // Optionaler Bibliotheks-Bild-Override statt eingebautem Icon (Baustein B3).
  customImageId: z6.string().uuid().optional(),
  // Dokumentierte Netzwerk-/Dienst-Ports (Baustein B4).
  servicePorts: z6.array(servicePortSchema).max(64).optional()
});
var DEVICE_FIELDS = {
  pc: { user: true, ip: true },
  laptop: { user: true, ip: true },
  printer: { user: false, ip: true },
  scanner: { user: false, ip: true },
  phone: { user: true, ip: true },
  accesspoint: { user: false, ip: true },
  camera: { user: false, ip: true },
  server: { user: false, ip: true },
  pbx: { user: false, ip: true },
  deskphone: { user: true, ip: true },
  other: { user: false, ip: true }
};
function deviceFields(kind) {
  return DEVICE_FIELDS[kind];
}
function isValidIpv4(value) {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(value);
  if (!m) return false;
  for (const octet of [m[1], m[2], m[3], m[4]]) {
    if (octet.length > 1 && octet[0] === "0") return false;
    if (Number(octet) > 255) return false;
  }
  return true;
}
function deviceInfoLines(node, vlans, users) {
  const lines = [];
  const label = node.label?.trim();
  if (label) lines.push({ type: "name", text: label });
  const device = node.device;
  if (!device) return lines;
  const vlan = findVlan(vlans, device.vlanRef);
  if (vlan) lines.push({ type: "vlan", vlanId: vlan.vlanId, color: vlan.color });
  if (device.ipMode) {
    const value = device.ipValue?.trim();
    lines.push({ type: "ip", mode: device.ipMode, value: value || void 0 });
  }
  const dns = device.dns?.trim();
  if (dns) lines.push({ type: "dns", value: dns });
  if (deviceFields(device.kind).user) {
    const ref = users ? findUser(users, device.userRef) : void 0;
    if (ref) {
      const suffix = ref.enabled === false ? " (deaktiviert)" : "";
      lines.push({ type: "user", value: userLabel(ref) + suffix });
    } else {
      const user = device.user?.trim();
      if (user) lines.push({ type: "user", value: user });
    }
  }
  if (device.servicePorts && device.servicePorts.length > 0) {
    lines.push({ type: "ports", text: device.servicePorts.map(formatServicePort).join(", ") });
  }
  return lines;
}

// src/host.ts
import { z as z7 } from "zod";
var vmDefSchema = z7.object({
  id: z7.string().max(64),
  name: z7.string().max(128),
  os: z7.string().max(64).optional(),
  apps: z7.string().max(255).optional(),
  ports: z7.string().max(128).optional(),
  vlanRef: z7.string().max(64).optional(),
  // Zugeordnetes Cluster-Volume (Baustein H); id eines VolumeDef, wie vlanRef fuer VLANs.
  volumeRef: z7.string().max(64).optional(),
  ip: z7.string().max(64).optional(),
  active: z7.boolean(),
  // Stabile Hyper-V/VMware-VM-ID (Merge-Schluessel beim CSV-Import).
  vmId: z7.string().max(64).optional(),
  // Hardware-Zuteilung der VM: CPU-Kerne (ganzzahlig) + RAM in GB.
  cores: z7.number().int().min(0).optional(),
  ram: z7.number().min(0).optional()
});
var hostDataSchema = z7.object({
  os: z7.string().max(64).optional(),
  hypervisor: z7.string().max(64).optional(),
  vms: z7.array(vmDefSchema).max(128),
  // Physische Host-Kapazität: CPU-Kerne (ganzzahlig) + RAM in GB.
  cores: z7.number().int().min(0).optional(),
  ram: z7.number().min(0).optional(),
  // Management-/Host-IP (frei setzbar oder per CSV-Import).
  ip: z7.string().max(64).optional()
});
function hostOvercommit(host) {
  let coresUsed = 0;
  let ramUsed = 0;
  for (const vm of host.vms) {
    if (!vm.active) continue;
    coresUsed += vm.cores ?? 0;
    ramUsed += vm.ram ?? 0;
  }
  return {
    cpu: host.cores != null && host.cores > 0 && coresUsed > host.cores,
    ram: host.ram != null && host.ram > 0 && ramUsed > host.ram,
    coresUsed,
    ramUsed
  };
}
var HOST_HEADER_H = 26;
var WARN_LINE_H = 13;
var HOST_PAD = 8;
var HOST_GAP = 6;
var TILE_W = 150;
var TILE_H = 76;
function hostTileLayout(count) {
  if (count <= 0) return { cols: 0, rows: 0 };
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  return { cols, rows };
}
function recommendedHostSize(count) {
  const layout = hostTileLayout(count);
  const cols = Math.max(2, layout.cols);
  const rows = Math.max(1, layout.rows);
  const width = HOST_PAD * 2 + cols * TILE_W + (cols - 1) * HOST_GAP;
  const height = HOST_HEADER_H + HOST_PAD + rows * TILE_H + (rows - 1) * HOST_GAP + HOST_PAD;
  return { width, height };
}
function hostContentTop(host) {
  const oc = hostOvercommit(host);
  const warnings = (oc.cpu ? 1 : 0) + (oc.ram ? 1 : 0);
  return HOST_HEADER_H + warnings * WARN_LINE_H;
}
function hostTileRects(box, count, topOffset = HOST_HEADER_H) {
  if (count <= 0) return [];
  const { cols, rows } = hostTileLayout(count);
  const areaX = box.x + HOST_PAD;
  const areaY = box.y + topOffset;
  const areaW = Math.max(1, box.width - 2 * HOST_PAD);
  const areaH = Math.max(1, box.height - topOffset - HOST_PAD);
  const colW = (areaW - (cols - 1) * HOST_GAP) / cols;
  const rowH = (areaH - (rows - 1) * HOST_GAP) / rows;
  const rects = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    rects.push({
      index: i,
      x: areaX + col * (colW + HOST_GAP),
      y: areaY + row * (rowH + HOST_GAP),
      width: Math.max(1, colW),
      height: Math.max(1, rowH)
    });
  }
  return rects;
}

// src/switch.ts
import { z as z8 } from "zod";
var SWITCH_LAYERS = [2, 3, 4];
var SWITCH_ROLES = ["access", "core"];
var PORT_KINDS = ["rj45", "sfp"];
var PORT_SPEEDS = ["100M", "1G", "2.5G", "10G", "25G", "40G", "100G"];
var SFP_MODULES = [
  "1000BASE-T",
  "1000BASE-SX (MM)",
  "1000BASE-LX (SM)",
  "10GBASE-SR (MM)",
  "10GBASE-LR (SM)",
  "DAC",
  "AOC"
];
var CABLE_TYPES = ["copper", "fiber"];
var FIBER_MODES = ["mm", "sm"];
var PORT_COUNTS = [0, 8, 12, 16, 24, 48];
var SFP_COUNTS = [0, 2, 4, 8, 12, 16, 24, 48];
var portDefSchema = z8.object({
  index: z8.number().int().min(1),
  portType: z8.enum(PORT_KINDS).optional(),
  speed: z8.enum(PORT_SPEEDS).optional(),
  vlanRef: z8.string().max(64).optional(),
  ip: z8.string().max(64).optional(),
  mac: z8.string().max(64).optional(),
  dose: z8.string().max(64).optional(),
  deviceId: z8.string().max(64).optional(),
  uplink: z8.boolean().optional(),
  service: z8.string().max(64).optional(),
  // Getaggte VLANs (C5.1): VLAN-ids aus scene.vlans; vlanRef bleibt Access/Untagged.
  taggedVlanRefs: z8.array(z8.string().max(64)).max(64).optional(),
  // SFP-Modul (C5.2): Freitext mit kuratierten Vorschlaegen (SFP_MODULES), nur bei SFP-Ports sinnvoll.
  sfpModule: z8.string().max(64).optional()
});
function resolveTaggedVlans(refs, vlans) {
  if (!refs || refs.length === 0) return [];
  const result = [];
  const seen = /* @__PURE__ */ new Set();
  for (const ref of refs) {
    if (seen.has(ref)) continue;
    const vlan = findVlan(vlans, ref);
    if (vlan) {
      seen.add(ref);
      result.push(vlan);
    }
  }
  return result;
}
var switchDataSchema = z8.object({
  portCount: z8.union([
    z8.literal(0),
    z8.literal(8),
    z8.literal(12),
    z8.literal(16),
    z8.literal(24),
    z8.literal(48)
  ]),
  sfpCount: z8.union([
    z8.literal(0),
    z8.literal(2),
    z8.literal(4),
    z8.literal(8),
    z8.literal(12),
    z8.literal(16),
    z8.literal(24),
    z8.literal(48)
  ]).optional(),
  layer: z8.union([z8.literal(2), z8.literal(3), z8.literal(4)]),
  role: z8.enum(SWITCH_ROLES),
  ports: z8.array(portDefSchema).max(96)
});
function buildPorts(data) {
  const existing = new Map((data.ports ?? []).map((p) => [p.index, p]));
  const total = data.portCount + (data.sfpCount ?? 0);
  const result = [];
  for (let index = 1; index <= total; index++) {
    const portType = index <= data.portCount ? "rj45" : "sfp";
    const prev = existing.get(index);
    result.push(prev ? { ...prev, index, portType } : { index, portType });
  }
  return result;
}
function isPortOccupied(p) {
  return !!(p.vlanRef || p.taggedVlanRefs && p.taggedVlanRefs.length > 0 || p.dose || p.deviceId || p.sfpModule);
}
function legendRows(data, vlans, showAll) {
  return [...data.ports].sort((a, b) => a.index - b.index).filter((p) => showAll || isPortOccupied(p)).map((p) => ({
    index: p.index,
    portType: p.portType ?? "rj45",
    accessVlan: findVlan(vlans, p.vlanRef),
    taggedVlans: resolveTaggedVlans(p.taggedVlanRefs, vlans),
    dose: p.dose
  }));
}
var SWITCH_TITLE_H = 18;
var SWITCH_PAD = 6;
var SWITCH_GAP = 3;
function switchPortRects(box, data) {
  const sfpCount = data.sfpCount ?? 0;
  const rjCols = data.portCount > 0 ? Math.ceil(data.portCount / 2) : 0;
  const sfpCols = sfpCount > 0 ? Math.ceil(sfpCount / 2) : 0;
  const totalCols = rjCols + sfpCols;
  const portsTop = box.y + SWITCH_TITLE_H + SWITCH_PAD;
  const portsBottom = box.y + box.height - SWITCH_PAD;
  const portH = Math.max(1, (portsBottom - portsTop - SWITCH_GAP) / 2);
  const topY = portsTop;
  const bottomY = portsTop + portH + SWITCH_GAP;
  const hasBoth = rjCols > 0 && sfpCols > 0;
  const innerW = Math.max(1, box.width - 2 * SWITCH_PAD - (hasBoth ? SWITCH_PAD : 0));
  const colW = totalCols > 0 ? innerW / totalCols : innerW;
  const startX = box.x + SWITCH_PAD;
  const slot = (index, slotCol, isTop, sfp, areaX) => ({
    index,
    x: areaX + slotCol * colW,
    y: isTop ? topY : bottomY,
    width: Math.max(1, colW - SWITCH_GAP),
    height: portH,
    row: isTop ? "top" : "bottom",
    sfp
  });
  const rects = [];
  for (let i = 1; i <= data.portCount; i++) {
    rects.push(slot(i, Math.floor((i - 1) / 2), (i - 1) % 2 === 0, false, startX));
  }
  const sfpStartX = startX + rjCols * colW + (hasBoth ? SWITCH_PAD : 0);
  for (let s = 0; s < sfpCount; s++) {
    rects.push(slot(data.portCount + s + 1, Math.floor(s / 2), s % 2 === 0, true, sfpStartX));
  }
  return rects;
}
function layerFields(layer) {
  return {
    vlan: true,
    mac: true,
    dose: true,
    speed: true,
    uplink: true,
    ip: layer >= 3,
    service: layer >= 4
  };
}
function recommendedSwitchWidth(portCount, sfpCount = 0) {
  const cols = Math.ceil(portCount / 2);
  const sfpCols = Math.ceil(sfpCount / 2);
  return Math.max(160, Math.round(cols * 20 + sfpCols * 22 + 24));
}
function portAnchorPoint(box, data, portIndex) {
  const r = switchPortRects(box, data).find((x) => x.index === portIndex);
  if (!r) return nodeCenter(box);
  return { x: r.x + r.width / 2, y: r.row === "top" ? r.y : r.y + r.height };
}
function deriveCableType(portType) {
  return portType === "sfp" ? "fiber" : "copper";
}
function cableColor(cableType, fiberMode) {
  if (cableType === "fiber") return fiberMode === "sm" ? "#eab308" : "#0891b2";
  return "#475569";
}

// src/firewall.ts
import { z as z9 } from "zod";
var FIREWALL_ROLES = ["firewall", "router", "utm"];
var IFACE_ZONES = ["wan", "lan", "dmz", "guest", "vpn", "other"];
var RULE_ACTIONS = ["allow", "deny", "reject"];
var RULE_PROTOCOLS = ["any", "tcp", "udp", "icmp", "esp", "gre"];
var VPN_KINDS = ["site-to-site", "remote-access"];
var VPN_PROTOCOLS = ["ipsec", "wireguard", "openvpn", "l2tp", "other"];
var HA_ROLES = ["standalone", "active", "passive"];
var firewallInterfaceSchema = z9.object({
  id: z9.string().max(64),
  name: z9.string().max(64),
  zone: z9.enum(IFACE_ZONES).optional(),
  ip: z9.string().max(64).optional(),
  // CIDR; Format prueft die UI (isValidIpv4Cidr), Schema nur Laenge.
  subnet: z9.string().max(64).optional(),
  vlanRef: z9.string().max(64).optional()
});
var firewallRuleSchema = z9.object({
  id: z9.string().max(64),
  // undefined = aktiv; nur explizit deaktivierte Regeln tragen false.
  enabled: z9.boolean().optional(),
  action: z9.enum(RULE_ACTIONS),
  source: z9.string().max(128).optional(),
  destination: z9.string().max(128).optional(),
  service: z9.string().max(64).optional(),
  protocol: z9.enum(RULE_PROTOCOLS).optional(),
  port: z9.string().max(64).optional(),
  interfaceId: z9.string().max(64).optional(),
  description: z9.string().max(255).optional()
});
var natRuleSchema = z9.object({
  id: z9.string().max(64),
  enabled: z9.boolean().optional(),
  name: z9.string().max(128).optional(),
  protocol: z9.enum(RULE_PROTOCOLS).optional(),
  interfaceId: z9.string().max(64).optional(),
  externalPort: z9.string().max(64).optional(),
  internalHost: z9.string().max(128).optional(),
  internalPort: z9.string().max(64).optional(),
  description: z9.string().max(255).optional()
});
var vpnDefSchema = z9.object({
  id: z9.string().max(64),
  name: z9.string().max(128),
  kind: z9.enum(VPN_KINDS),
  protocol: z9.enum(VPN_PROTOCOLS).optional(),
  peer: z9.string().max(128).optional(),
  localSubnets: z9.string().max(255).optional(),
  remoteSubnets: z9.string().max(255).optional(),
  active: z9.boolean().optional()
});
var firewallDataSchema = z9.object({
  role: z9.enum(FIREWALL_ROLES),
  // Hersteller/Modell rein dokumentarisch (kein Katalog).
  model: z9.string().max(128).optional(),
  // undefined = 'standalone'.
  haRole: z9.enum(HA_ROLES).optional(),
  // Referenz auf die Partner-Firewall (node.id), nur bei active/passive sinnvoll.
  haPeerNodeId: z9.string().max(64).optional(),
  interfaces: z9.array(firewallInterfaceSchema).max(64),
  rules: z9.array(firewallRuleSchema).max(512),
  nat: z9.array(natRuleSchema).max(256),
  vpns: z9.array(vpnDefSchema).max(128)
});
function zoneColor(zone) {
  switch (zone) {
    case "wan":
      return "#dc2626";
    case "lan":
      return "#16a34a";
    case "dmz":
      return "#f59e0b";
    case "guest":
      return "#3b82f6";
    case "vpn":
      return "#8b5cf6";
    default:
      return "#64748b";
  }
}
function recommendedFirewallWidth(interfaceCount) {
  return Math.max(240, Math.round(interfaceCount * 60 + 24));
}
var FW_TITLE_H = 34;
var FW_FOOTER_H = 14;
var FW_IFACE_H = 26;
var FW_PAD = 6;
var FW_GAP = 4;
function firewallInterfaceRects(box, interfaces) {
  const n = interfaces.length;
  if (n === 0) return [];
  const areaX = box.x + FW_PAD;
  const areaW = Math.max(1, box.width - 2 * FW_PAD);
  const colW = areaW / n;
  const y = box.y + Math.max(FW_TITLE_H, box.height - FW_FOOTER_H - FW_PAD - FW_IFACE_H);
  return interfaces.map((f, i) => ({
    id: f.id,
    index: i + 1,
    x: areaX + i * colW,
    y,
    width: Math.max(1, colW - FW_GAP),
    height: FW_IFACE_H
  }));
}
function firewallAnchorPoint(box, interfaces, portIndex) {
  const r = firewallInterfaceRects(box, interfaces).find((x) => x.index === portIndex);
  if (!r) return nodeCenter(box);
  return { x: r.x + r.width / 2, y: r.y + r.height };
}
function interfaceAnchorIndex(interfaces, id) {
  const i = interfaces.findIndex((f) => f.id === id);
  return i >= 0 ? i + 1 : void 0;
}
function firewallRuleCounts(data) {
  return { rules: data.rules.length, nat: data.nat.length, vpns: data.vpns.length };
}
function firewallLegendRules(data, showDisabled) {
  return data.rules.filter((r) => showDisabled || r.enabled !== false);
}
function firewallLegendNat(data, showDisabled) {
  return data.nat.filter((r) => showDisabled || r.enabled !== false);
}
function firewallLegendVpns(data) {
  return [...data.vpns];
}

// src/cluster-storage.ts
import { z as z10 } from "zod";
var VOLUME_REDUNDANCY = ["none", "mirror-2", "mirror-3", "parity", "parity-dual"];
var VOLUME_WARN_DEFAULT = 80;
var VOLUME_COLORS = [
  "#2563eb",
  // blue
  "#dc2626",
  // red
  "#16a34a",
  // green
  "#ca8a04",
  // yellow
  "#7c3aed",
  // violet
  "#0d9488",
  // teal
  "#ea580c",
  // orange
  "#db2777",
  // pink
  "#c026d3",
  // fuchsia
  "#475569"
  // slate
];
var volumeDefSchema = z10.object({
  id: z10.string().max(64),
  name: z10.string().max(128),
  fs: z10.string().max(32).optional(),
  sizeGb: z10.number().min(0).optional(),
  usedGb: z10.number().min(0).optional(),
  redundancy: z10.enum(VOLUME_REDUNDANCY).optional(),
  note: z10.string().max(255).optional(),
  color: z10.string().max(32),
  // Warnschwelle in Prozent (Default VOLUME_WARN_DEFAULT); warnOff pausiert die Warnung,
  // erhaelt aber den eingestellten Schwellwert.
  warnAtPercent: z10.number().int().min(0).max(100).optional(),
  warnOff: z10.boolean().optional()
});
var CLUSTER_STORAGE_KINDS = ["s2d", "san-iscsi", "san-fc", "smb", "other"];
var clusterStorageDataSchema = z10.object({
  cluster: z10.string().max(128).optional(),
  kind: z10.enum(CLUSTER_STORAGE_KINDS).optional(),
  // ISO-Datum 'YYYY-MM-DD' des Imports -> Kopfzeile 'Stand: ...'.
  importedAt: z10.string().max(32).optional(),
  volumes: z10.array(volumeDefSchema).max(64)
});
function volumeUsage(vol) {
  const size = vol.sizeGb ?? 0;
  if (size <= 0) return { percent: 0, warn: false };
  const raw = (vol.usedGb ?? 0) / size * 100;
  const percent = Math.min(100, Math.max(0, Math.round(raw)));
  const threshold = vol.warnAtPercent ?? VOLUME_WARN_DEFAULT;
  const warn = !vol.warnOff && raw >= threshold;
  return { percent, warn };
}
function nextVolumeColor(existing) {
  const used = new Set(existing.map((v) => v.color));
  const free = VOLUME_COLORS.find((c) => !used.has(c));
  return free ?? VOLUME_COLORS[existing.length % VOLUME_COLORS.length];
}
var CS_HEADER_H = 30;
var CS_WARN_LINE_H = 13;
var CS_PAD = 8;
var CS_GAP = 6;
var VOL_TILE_W = 130;
var VOL_TILE_H = 74;
function volumeTileLayout(count) {
  if (count <= 0) return { cols: 0, rows: 0 };
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  return { cols, rows };
}
function recommendedClusterStorageSize(count) {
  const layout = volumeTileLayout(count);
  const cols = Math.max(2, layout.cols);
  const rows = Math.max(1, layout.rows);
  const width = CS_PAD * 2 + cols * VOL_TILE_W + (cols - 1) * CS_GAP;
  const height = CS_HEADER_H + CS_PAD + rows * VOL_TILE_H + (rows - 1) * CS_GAP + CS_PAD;
  return { width, height };
}
function clusterStorageContentTop(data) {
  const warnings = data.volumes.filter((v) => volumeUsage(v).warn).length;
  return CS_HEADER_H + warnings * CS_WARN_LINE_H;
}
function volumeTileRects(box, count, topOffset = CS_HEADER_H) {
  if (count <= 0) return [];
  const { cols, rows } = volumeTileLayout(count);
  const areaX = box.x + CS_PAD;
  const areaY = box.y + topOffset;
  const areaW = Math.max(1, box.width - 2 * CS_PAD);
  const areaH = Math.max(1, box.height - topOffset - CS_PAD);
  const colW = (areaW - (cols - 1) * CS_GAP) / cols;
  const rowH = (areaH - (rows - 1) * CS_GAP) / rows;
  const rects = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    rects.push({
      index: i,
      x: areaX + col * (colW + CS_GAP),
      y: areaY + row * (rowH + CS_GAP),
      width: Math.max(1, colW),
      height: Math.max(1, rowH)
    });
  }
  return rects;
}
function sceneVolumes(nodes) {
  const out = [];
  for (const n of nodes) {
    if (n.type !== "clusterstorage" || !n.clusterStorage) continue;
    for (const volume of n.clusterStorage.volumes) {
      out.push({ volume, storageLabel: n.label ?? "", storageNodeId: n.id });
    }
  }
  return out;
}
function findVolume(nodes, ref) {
  if (!ref) return void 0;
  for (const n of nodes) {
    if (n.type !== "clusterstorage" || !n.clusterStorage) continue;
    const hit = n.clusterStorage.volumes.find((v) => v.id === ref);
    if (hit) return hit;
  }
  return void 0;
}
function volumeVmCount(nodes, volumeId) {
  let count = 0;
  for (const n of nodes) {
    if (n.type !== "host" || !n.host) continue;
    for (const vm of n.host.vms) {
      if (vm.volumeRef === volumeId) count++;
    }
  }
  return count;
}

// src/scene.ts
var NODE_TYPES = ["zone", "text", "circle", "cloud", "rect", "image", "vlan-legend", "switch-legend", "device", "switch", "host", "firewall", "firewall-legend", "clusterstorage"];
var ANCHOR_PORTS = ["auto", "top", "right", "bottom", "left"];
var sceneEndpointSchema = z11.union([
  z11.object({
    nodeId: z11.string().max(64),
    port: z11.enum(ANCHOR_PORTS).optional(),
    // Andocken an genau einen Switch-Port (1-basiert; Baustein C).
    portIndex: z11.number().int().min(1).optional()
  }),
  z11.object({ x: z11.number(), y: z11.number() })
]);
var TEXT_ALIGNS = ["left", "center", "right"];
var LABEL_POSITIONS = [
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "middle-center",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right"
];
var switchLegendDataSchema = z11.object({
  sourceNodeId: z11.string().max(64).optional(),
  // default (undefined/false) -> nur belegte Ports; true -> alle Ports.
  showAllPorts: z11.boolean().optional()
});
var firewallLegendDataSchema = z11.object({
  sourceNodeId: z11.string().max(64).optional(),
  // Welche Tabellen gezeigt werden; default (undefined) -> ['rules'].
  sections: z11.array(z11.enum(["rules", "nat", "vpns"])).optional(),
  // default false -> deaktivierte Regeln/NAT ausblenden.
  showDisabled: z11.boolean().optional()
});
var sceneNodeSchema = z11.object({
  id: z11.string().max(64),
  type: z11.enum(NODE_TYPES),
  x: z11.number(),
  y: z11.number(),
  width: z11.number().min(1).max(1e4),
  height: z11.number().min(1).max(1e4),
  rotation: z11.number().optional(),
  // bis 1000 Zeichen → mehrzeilige Beschriftungen
  label: z11.string().max(1e3).default(""),
  environmentType: z11.enum(ENVIRONMENT_TYPES).default("neutral"),
  fill: z11.string().max(32).optional(),
  stroke: z11.string().max(32).optional(),
  textColor: z11.string().max(32).optional(),
  // Alt-Feld; per labelAlignment() zusammen mit labelPosition ausgewertet.
  align: z11.enum(TEXT_ALIGNS).optional(),
  labelPosition: z11.enum(LABEL_POSITIONS).optional(),
  fontSize: z11.number().min(6).max(96).optional(),
  imageId: z11.string().uuid().optional(),
  // Flache Gruppierung: Knoten mit gleicher groupId bilden eine Gruppe.
  groupId: z11.string().max(64).optional(),
  // Typspezifische Geraetedaten (nur bei type === 'device').
  device: deviceDataSchema.optional(),
  // Typspezifische Switch-Daten (nur bei type === 'switch').
  switch: switchDataSchema.optional(),
  // Typspezifische Host/VM-Daten (nur bei type === 'host').
  host: hostDataSchema.optional(),
  // Typspezifische Clusterstorage-Daten (nur bei type === 'clusterstorage').
  clusterStorage: clusterStorageDataSchema.optional(),
  // Typspezifische Switch-Legenden-Daten (nur bei type === 'switch-legend').
  switchLegend: switchLegendDataSchema.optional(),
  // Typspezifische Firewall/Router-Daten (nur bei type === 'firewall').
  firewall: firewallDataSchema.optional(),
  // Typspezifische Firewall-Legenden-Daten (nur bei type === 'firewall-legend').
  firewallLegend: firewallLegendDataSchema.optional()
});
var LINE_STYLES = ["solid", "dashed", "dotted"];
var CONNECTOR_ROUTINGS = ["straight", "orthogonal"];
var BRIDGE_MODES = ["default", "over", "never"];
var connectorSchema = z11.object({
  id: z11.string().max(64),
  from: sceneEndpointSchema,
  to: sceneEndpointSchema,
  // label = oberhalb der Linie, labelBelow = unterhalb.
  label: z11.string().max(200).optional(),
  labelBelow: z11.string().max(200).optional(),
  // Alt-Feld; per connectorLineStyle() auf lineStyle abgebildet.
  dashed: z11.boolean().optional(),
  arrowStart: z11.boolean().optional(),
  arrowEnd: z11.boolean().optional(),
  stroke: z11.string().max(32).optional(),
  strokeWidth: z11.number().min(0.5).max(20).optional(),
  lineStyle: z11.enum(LINE_STYLES).optional(),
  routing: z11.enum(CONNECTOR_ROUTINGS).optional(),
  // Position des Knicks bei orthogonalem Routing (0..1, Standard 0.5).
  bendRatio: z11.number().min(0).max(1).optional(),
  // Kabel-/LWL-Metadaten (Baustein C3).
  cableType: z11.enum(CABLE_TYPES).optional(),
  fiberMode: z11.enum(FIBER_MODES).optional(),
  cableSpeed: z11.enum(PORT_SPEEDS).optional(),
  // Brücken-Override pro Linie (Baustein D); undefined = globale Konvention.
  bridge: z11.enum(BRIDGE_MODES).optional()
});
function connectorLineStyle(connector) {
  return connector.lineStyle ?? (connector.dashed ? "dashed" : "solid");
}
function labelAlignment(node) {
  if (node.labelPosition) {
    const [vertical, horizontal] = node.labelPosition.split("-");
    return { align: horizontal, verticalAlign: vertical };
  }
  return { align: node.align ?? "center", verticalAlign: "middle" };
}
var LABEL_POSITION_TYPES = /* @__PURE__ */ new Set(["text", "circle", "cloud", "rect"]);
function supportsLabelPosition(type) {
  return LABEL_POSITION_TYPES.has(type);
}
var canvasMetaSchema = z11.object({
  width: z11.number().min(100).max(2e4),
  height: z11.number().min(100).max(2e4),
  format: z11.enum(["a4-l", "a4-p", "a3-l", "a3-p", "free"]).optional()
});
var commentAnchorSchema = z11.union([
  z11.object({ nodeId: z11.string().max(64) }),
  z11.object({ x: z11.number(), y: z11.number() })
]);
var sceneCommentSchema = z11.object({
  id: z11.string().max(64),
  authorId: z11.string().max(64),
  authorName: z11.string().max(200),
  anchor: commentAnchorSchema,
  text: z11.string().min(1).max(4e3),
  // ISO-8601 (UTC); als String, damit JSON/Y.Map ohne Sonderbehandlung durchreichen.
  createdAt: z11.string().max(64),
  resolved: z11.boolean().default(false)
});
var sceneDocSchema = z11.object({
  version: z11.literal(1),
  nodes: z11.array(sceneNodeSchema).max(1e3),
  connectors: z11.array(connectorSchema).max(2e3),
  canvas: canvasMetaSchema.optional(),
  vlans: z11.array(vlanDefSchema).max(256).optional(),
  // Zentrales Benutzer-Verzeichnis (Baustein G), per CSV aus Azure/Entra befuellt.
  users: z11.array(userDefSchema).max(5e3).optional(),
  // Globaler Schalter für Linien-Brücken (Baustein D); undefined = an.
  lineBridges: z11.boolean().optional(),
  // Kommentar-Pins (Kollaboration Phase 5); Teil des Dokuments, nicht der Awareness.
  comments: z11.array(sceneCommentSchema).max(2e3).optional()
});
var emptyScene = { version: 1, nodes: [], connectors: [] };

// src/scene-yjs.ts
import * as Y from "yjs";
var Y_META_KEY = "meta";
var Y_NODES_KEY = "nodes";
var Y_CONNECTORS_KEY = "connectors";
var Y_VLANS_KEY = "vlans";
var Y_USERS_KEY = "users";
var Y_COMMENTS_KEY = "comments";
function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}
function syncYMap(map, target) {
  for (const key of [...map.keys()]) {
    if (target[key] === void 0) map.delete(key);
  }
  for (const [key, value] of Object.entries(target)) {
    if (value === void 0) continue;
    if (JSON.stringify(map.get(key)) !== JSON.stringify(value)) map.set(key, cloneJson(value));
  }
}
function yMapToPlain(map) {
  const result = {};
  for (const [key, value] of map.entries()) result[key] = value;
  return result;
}
function syncYArray(arr, targets) {
  const targetIds = new Set(targets.map((t) => String(t.id)));
  for (let i = arr.length - 1; i >= 0; i--) {
    if (!targetIds.has(String(arr.get(i).get("id")))) arr.delete(i, 1);
  }
  for (let i = 0; i < targets.length; i++) {
    const target = targets[i];
    const existing = i < arr.length ? arr.get(i) : void 0;
    if (existing && existing.get("id") === target.id) {
      syncYMap(existing, target);
      continue;
    }
    for (let j = i + 1; j < arr.length; j++) {
      if (arr.get(j).get("id") === target.id) {
        arr.delete(j, 1);
        break;
      }
    }
    const fresh = new Y.Map();
    arr.insert(i, [fresh]);
    syncYMap(fresh, target);
  }
  if (arr.length > targets.length) arr.delete(targets.length, arr.length - targets.length);
}
function applySceneToYDoc(doc, next, origin) {
  doc.transact(() => {
    syncYMap(doc.getMap(Y_META_KEY), {
      version: next.version,
      canvas: next.canvas,
      lineBridges: next.lineBridges
    });
    syncYArray(doc.getArray(Y_NODES_KEY), next.nodes.map((n) => cloneJson(n)));
    syncYArray(doc.getArray(Y_CONNECTORS_KEY), next.connectors.map((c) => cloneJson(c)));
    syncYArray(doc.getArray(Y_VLANS_KEY), (next.vlans ?? []).map((v) => cloneJson(v)));
    syncYArray(doc.getArray(Y_USERS_KEY), (next.users ?? []).map((u) => cloneJson(u)));
    syncYArray(doc.getArray(Y_COMMENTS_KEY), (next.comments ?? []).map((k) => cloneJson(k)));
  }, origin);
}
function sceneDocToYDoc(scene, doc = new Y.Doc()) {
  applySceneToYDoc(doc, scene);
  return doc;
}
function yDocToSceneDoc(doc) {
  const meta = doc.getMap(Y_META_KEY);
  const readArray = (key) => doc.getArray(key).map((m) => yMapToPlain(m));
  const vlans = readArray(Y_VLANS_KEY);
  const users = readArray(Y_USERS_KEY);
  const comments = readArray(Y_COMMENTS_KEY);
  const scene = {
    version: 1,
    nodes: readArray(Y_NODES_KEY),
    connectors: readArray(Y_CONNECTORS_KEY),
    ...meta.get("canvas") !== void 0 ? { canvas: meta.get("canvas") } : {},
    ...meta.get("lineBridges") !== void 0 ? { lineBridges: meta.get("lineBridges") } : {},
    ...vlans.length > 0 ? { vlans } : {},
    ...users.length > 0 ? { users } : {},
    ...comments.length > 0 ? { comments } : {}
  };
  return scene;
}

// src/scene-comments.ts
function commentAnchorKey(anchor) {
  return "nodeId" in anchor ? `node:${anchor.nodeId}` : `pt:${anchor.x}:${anchor.y}`;
}
function groupCommentThreads(comments) {
  const byKey = /* @__PURE__ */ new Map();
  for (const comment of comments) {
    const key = commentAnchorKey(comment.anchor);
    const list = byKey.get(key);
    if (list) list.push(comment);
    else byKey.set(key, [comment]);
  }
  return [...byKey.entries()].map(([key, list]) => {
    const sorted = [...list].sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)
    );
    return {
      key,
      anchor: sorted[0].anchor,
      comments: sorted,
      resolved: sorted.every((c) => c.resolved)
    };
  });
}
function commentThreadPosition(thread, nodes) {
  const anchor = thread.anchor;
  if ("nodeId" in anchor) {
    const node = nodes.find((n) => n.id === anchor.nodeId);
    if (!node) return null;
    return { x: node.x + node.width, y: node.y };
  }
  return { x: anchor.x, y: anchor.y };
}

// src/presence.ts
import { z as z12 } from "zod";
var PRESENCE_COLORS = [
  "#dc2626",
  // red
  "#ea580c",
  // orange
  "#ca8a04",
  // yellow
  "#16a34a",
  // green
  "#0d9488",
  // teal
  "#2563eb",
  // blue
  "#7c3aed",
  // violet
  "#c026d3",
  // fuchsia
  "#db2777",
  // pink
  "#475569"
  // slate
];
var PRESENCE_MAX_SELECTION = 500;
function presenceColor(userId) {
  let hash = 2166136261;
  for (let i = 0; i < userId.length; i++) {
    hash ^= userId.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return PRESENCE_COLORS[hash % PRESENCE_COLORS.length];
}
function presenceInitials(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}
var presenceViewportSchema = z12.object({
  x: z12.number(),
  y: z12.number(),
  scale: z12.number().positive().max(100)
});
var presenceStateSchema = z12.object({
  userId: z12.string().min(1),
  name: z12.string().min(1).max(200),
  color: z12.string().max(32),
  cursor: z12.object({ x: z12.number(), y: z12.number() }).nullish(),
  // IDs der ausgewaehlten Objekte (Knoten UND Verbinder); fehlend/null = keine Auswahl
  // bzw. Phase-2-Client ohne dieses Feld.
  selection: z12.array(z12.string().min(1).max(64)).max(PRESENCE_MAX_SELECTION).nullish(),
  // Phase 4 (Follow-Modus): eigener Sichtbereich und wem man gerade folgt.
  viewport: presenceViewportSchema.nullish(),
  followingUserId: z12.string().max(64).nullish()
});
function remoteSelectionColors(others) {
  const map = /* @__PURE__ */ new Map();
  for (const p of [...others].sort((a, b) => a.clientId - b.clientId)) {
    for (const id of p.selection ?? []) {
      if (!map.has(id)) map.set(id, p.color);
    }
  }
  return map;
}
function viewportCenterWorld(stagePos, scale, size) {
  return { x: (size.width / 2 - stagePos.x) / scale, y: (size.height / 2 - stagePos.y) / scale };
}
function stagePosForCenter(center, scale, size) {
  return { x: size.width / 2 - center.x * scale, y: size.height / 2 - center.y * scale };
}
function followedViewport(others, userId) {
  let best = null;
  for (const p of others) {
    if (p.userId !== userId || !p.viewport) continue;
    if (!best || p.clientId < best.clientId) best = p;
  }
  return best?.viewport ?? null;
}

// src/csv-import.ts
var IMPORT_SOURCES = ["azure-users", "hyperv-vms", "vmware-vms", "hyperv-storage"];
var MERGE_STRATEGIES = ["merge", "mirror", "add-new"];
function parseRecords(s, delim) {
  const records = [];
  let field = "";
  let record = [];
  let inQuotes = false;
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === delim) {
      record.push(field);
      field = "";
      i++;
      continue;
    }
    if (c === "\r") {
      i++;
      continue;
    }
    if (c === "\n") {
      record.push(field);
      records.push(record);
      field = "";
      record = [];
      i++;
      continue;
    }
    field += c;
    i++;
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records;
}
function parseCsv(text) {
  const s = text.replace(/^\uFEFF/, "");
  if (s.trim() === "") return { headers: [], rows: [] };
  const nl = s.search(/\r?\n/);
  const firstLine = nl === -1 ? s : s.slice(0, nl);
  const semis = firstLine.split(";").length - 1;
  const commas = firstLine.split(",").length - 1;
  const delim = semis >= commas ? ";" : ",";
  const records = parseRecords(s, delim);
  if (records.length === 0) return { headers: [], rows: [] };
  const headers = records[0].map((h) => h.trim());
  const rows = records.slice(1).filter((r) => r.some((c) => c.trim() !== "")).map((r) => {
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = (r[i] ?? "").trim();
    });
    return obj;
  });
  return { headers, rows };
}
function pick(row, name) {
  const key = Object.keys(row).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? row[key] : "";
}
function boolFrom(v) {
  const s = v.trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes" || s === "ja") return true;
  if (s === "false" || s === "0" || s === "no" || s === "nein") return false;
  return void 0;
}
function intFrom(v) {
  const n = Number.parseInt(v.trim(), 10);
  return Number.isFinite(n) ? n : void 0;
}
function numFrom(v) {
  const n = Number.parseFloat(v.trim().replace(",", "."));
  return Number.isFinite(n) ? n : void 0;
}
var idFallback = 0;
function genId() {
  const cryptoApi = globalThis.crypto;
  return cryptoApi?.randomUUID?.() ?? `imp-${Date.now()}-${idFallback++}`;
}
function mapUserRows(rows) {
  const out = [];
  for (const row of rows) {
    const id = pick(row, "Id");
    const displayName = pick(row, "DisplayName");
    if (!id || !displayName) continue;
    const upn = pick(row, "UserPrincipalName") || void 0;
    const department = pick(row, "Department") || void 0;
    const jobTitle = pick(row, "JobTitle") || void 0;
    const enabled = boolFrom(pick(row, "AccountEnabled"));
    out.push({
      id,
      displayName,
      ...upn ? { upn } : {},
      ...department ? { department } : {},
      ...jobTitle ? { jobTitle } : {},
      ...enabled !== void 0 ? { enabled } : {}
    });
  }
  return out;
}
function mapVmRows(rows) {
  const out = [];
  for (const row of rows) {
    const host = pick(row, "Host");
    const name = pick(row, "VMName");
    if (!host || !name) continue;
    const vmId = pick(row, "VMId") || void 0;
    const os = pick(row, "OS") || void 0;
    const cores = intFrom(pick(row, "Cores"));
    const ram = numFrom(pick(row, "RAM_GB"));
    const ip = pick(row, "IP") || void 0;
    const active = pick(row, "State").trim().toLowerCase() === "running";
    const hostCores = intFrom(pick(row, "HostCores"));
    const hostRam = numFrom(pick(row, "HostRAM_GB"));
    const hostIp = pick(row, "HostIP") || void 0;
    const volumeName = pick(row, "Volume") || void 0;
    const vm = {
      id: genId(),
      name,
      active,
      ...vmId ? { vmId } : {},
      ...os ? { os } : {},
      ...cores !== void 0 ? { cores } : {},
      ...ram !== void 0 ? { ram } : {},
      ...ip ? { ip } : {}
    };
    out.push({
      host,
      ...hostCores !== void 0 ? { hostCores } : {},
      ...hostRam !== void 0 ? { hostRam } : {},
      ...hostIp ? { hostIp } : {},
      ...volumeName ? { volumeName } : {},
      vm
    });
  }
  return out;
}
function mergeUsers(existing, imported, strategy) {
  const existingIds = new Set(existing.map((u) => u.id));
  const importedIds = new Set(imported.map((u) => u.id));
  const recognized = imported.length;
  if (strategy === "add-new") {
    let added2 = 0;
    const users2 = [...existing];
    for (const imp of imported) {
      if (!existingIds.has(imp.id)) {
        users2.push(imp);
        added2++;
      }
    }
    return { users: users2, diff: { recognized, added: added2, updated: 0, removed: 0 } };
  }
  if (strategy === "mirror") {
    const added2 = imported.filter((u) => !existingIds.has(u.id)).length;
    const updated2 = imported.filter((u) => existingIds.has(u.id)).length;
    const removed = existing.filter((u) => !importedIds.has(u.id)).length;
    return { users: [...imported], diff: { recognized, added: added2, updated: updated2, removed } };
  }
  const importedById = new Map(imported.map((u) => [u.id, u]));
  let updated = 0;
  const users = existing.map((ex) => {
    const imp = importedById.get(ex.id);
    if (imp) {
      updated++;
      return imp;
    }
    return ex;
  });
  let added = 0;
  for (const imp of imported) {
    if (!existingIds.has(imp.id)) {
      users.push(imp);
      added++;
    }
  }
  return { users, diff: { recognized, added, updated, removed: 0 } };
}
function vmKey(vm) {
  return vm.vmId ? `id:${vm.vmId}` : `name:${vm.name.trim().toLowerCase()}`;
}
function mergeVm(ex, imp) {
  return {
    ...ex,
    name: imp.name,
    active: imp.active,
    vmId: imp.vmId ?? ex.vmId,
    os: imp.os ?? ex.os,
    cores: imp.cores ?? ex.cores,
    ram: imp.ram ?? ex.ram,
    ip: imp.ip ?? ex.ip,
    volumeRef: imp.volumeRef ?? ex.volumeRef
  };
}
function mergeVms(existing, imported, strategy) {
  const existingByKey = new Map(existing.map((v) => [vmKey(v), v]));
  const importedByKey = new Map(imported.map((v) => [vmKey(v), v]));
  const recognized = imported.length;
  if (strategy === "add-new") {
    let added2 = 0;
    const vms2 = [...existing];
    for (const imp of imported) {
      if (!existingByKey.has(vmKey(imp))) {
        vms2.push(imp);
        added2++;
      }
    }
    return { vms: vms2, diff: { recognized, added: added2, updated: 0, removed: 0 } };
  }
  if (strategy === "mirror") {
    let added2 = 0;
    let updated2 = 0;
    const vms2 = [];
    for (const imp of imported) {
      const ex = existingByKey.get(vmKey(imp));
      if (ex) {
        vms2.push(mergeVm(ex, imp));
        updated2++;
      } else {
        vms2.push(imp);
        added2++;
      }
    }
    const removed = existing.filter((v) => !importedByKey.has(vmKey(v))).length;
    return { vms: vms2, diff: { recognized, added: added2, updated: updated2, removed } };
  }
  let updated = 0;
  const vms = existing.map((ex) => {
    const imp = importedByKey.get(vmKey(ex));
    if (imp) {
      updated++;
      return mergeVm(ex, imp);
    }
    return ex;
  });
  let added = 0;
  for (const imp of imported) {
    if (!existingByKey.has(vmKey(imp))) {
      vms.push(imp);
      added++;
    }
  }
  return { vms, diff: { recognized, added, updated, removed: 0 } };
}
function redundancyFrom(v) {
  const s = v.trim();
  return VOLUME_REDUNDANCY.includes(s) ? s : void 0;
}
function mapVolumeRows(rows) {
  const out = [];
  const assigned = [];
  for (const row of rows) {
    const cluster = pick(row, "Cluster");
    const name = pick(row, "Volume");
    if (!cluster || !name) continue;
    const fs = pick(row, "FS") || void 0;
    const sizeGb = numFrom(pick(row, "Size_GB"));
    const usedGb = numFrom(pick(row, "Used_GB"));
    const redundancy = redundancyFrom(pick(row, "Redundancy"));
    const exportedAt = pick(row, "ExportedAt") || void 0;
    const color = nextVolumeColor(assigned);
    assigned.push({ color });
    const volume = {
      id: genId(),
      name,
      color,
      ...fs ? { fs } : {},
      ...sizeGb !== void 0 ? { sizeGb } : {},
      ...usedGb !== void 0 ? { usedGb } : {},
      ...redundancy ? { redundancy } : {}
    };
    out.push({ cluster, ...exportedAt ? { exportedAt } : {}, volume });
  }
  return out;
}
function volumeKey(v) {
  return v.name.trim().toLowerCase();
}
function mergeVolume(ex, imp) {
  return {
    ...ex,
    name: imp.name,
    fs: imp.fs ?? ex.fs,
    sizeGb: imp.sizeGb ?? ex.sizeGb,
    usedGb: imp.usedGb ?? ex.usedGb,
    redundancy: imp.redundancy ?? ex.redundancy
  };
}
function mergeVolumes(existing, imported, strategy) {
  const existingByKey = new Map(existing.map((v) => [volumeKey(v), v]));
  const importedByKey = new Map(imported.map((v) => [volumeKey(v), v]));
  const recognized = imported.length;
  if (strategy === "add-new") {
    let added2 = 0;
    const volumes2 = [...existing];
    for (const imp of imported) {
      if (!existingByKey.has(volumeKey(imp))) {
        volumes2.push(imp);
        added2++;
      }
    }
    return { volumes: volumes2, diff: { recognized, added: added2, updated: 0, removed: 0 } };
  }
  if (strategy === "mirror") {
    let added2 = 0;
    let updated2 = 0;
    const volumes2 = [];
    for (const imp of imported) {
      const ex = existingByKey.get(volumeKey(imp));
      if (ex) {
        volumes2.push(mergeVolume(ex, imp));
        updated2++;
      } else {
        volumes2.push(imp);
        added2++;
      }
    }
    const removed = existing.filter((v) => !importedByKey.has(volumeKey(v))).length;
    return { volumes: volumes2, diff: { recognized, added: added2, updated: updated2, removed } };
  }
  let updated = 0;
  const volumes = existing.map((ex) => {
    const imp = importedByKey.get(volumeKey(ex));
    if (imp) {
      updated++;
      return mergeVolume(ex, imp);
    }
    return ex;
  });
  let added = 0;
  for (const imp of imported) {
    if (!existingByKey.has(volumeKey(imp))) {
      volumes.push(imp);
      added++;
    }
  }
  return { volumes, diff: { recognized, added, updated, removed: 0 } };
}

// src/line-bridges.ts
var EPS = 1e-9;
var DEFAULT_RADIUS = 6;
var DEFAULT_SEGMENTS = 8;
function segmentIntersection(a1, a2, b1, b2) {
  const rX = a2.x - a1.x;
  const rY = a2.y - a1.y;
  const sX = b2.x - b1.x;
  const sY = b2.y - b1.y;
  const denom = rX * sY - rY * sX;
  if (Math.abs(denom) < EPS) return null;
  const t = ((b1.x - a1.x) * sY - (b1.y - a1.y) * sX) / denom;
  const u = ((b1.x - a1.x) * rY - (b1.y - a1.y) * rX) / denom;
  if (t <= EPS || t >= 1 - EPS || u <= EPS || u >= 1 - EPS) return null;
  return { x: a1.x + t * rX, y: a1.y + t * rY };
}
function jumpPriority(mode) {
  if (mode === "over") return 1;
  if (mode === "never") return -1;
  return 0;
}
function isHorizontal(p, q) {
  return Math.abs(q.x - p.x) >= Math.abs(q.y - p.y);
}
function computeLineBridges(connectors, opts = {}) {
  if (opts.enabled === false) return [];
  const radius = opts.radius ?? DEFAULT_RADIUS;
  const markers = [];
  for (let i = 0; i < connectors.length; i++) {
    const a = connectors[i];
    const pa = jumpPriority(a.bridge);
    for (let j = i + 1; j < connectors.length; j++) {
      const b = connectors[j];
      const pb = jumpPriority(b.bridge);
      if (pa < 0 && pb < 0) continue;
      for (let si = 0; si < a.points.length - 1; si++) {
        const a1 = a.points[si];
        const a2 = a.points[si + 1];
        for (let sj = 0; sj < b.points.length - 1; sj++) {
          const b1 = b.points[sj];
          const b2 = b.points[sj + 1];
          const center = segmentIntersection(a1, a2, b1, b2);
          if (!center) continue;
          let jumpA;
          if (pa < 0) jumpA = false;
          else if (pb < 0) jumpA = true;
          else if (pa !== pb) jumpA = pa > pb;
          else {
            const ha = isHorizontal(a1, a2);
            const hb = isHorizontal(b1, b2);
            if (ha !== hb) jumpA = ha;
            else jumpA = false;
          }
          markers.push(
            jumpA ? { connectorId: a.id, segmentIndex: si, center, radius } : { connectorId: b.id, segmentIndex: sj, center, radius }
          );
        }
      }
    }
  }
  return markers;
}
function upperNormal(dx, dy, len) {
  const n1 = { x: -dy / len, y: dx / len };
  const n2 = { x: dy / len, y: -dx / len };
  if (Math.abs(n1.y - n2.y) > EPS) return n1.y < n2.y ? n1 : n2;
  return n1.x >= n2.x ? n1 : n2;
}
function applyBridges(points, markers, opts = {}) {
  if (markers.length === 0 || points.length < 2) return points;
  const segments = opts.segments ?? DEFAULT_SEGMENTS;
  const result = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i];
    const q = points[i + 1];
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const len = Math.hypot(dx, dy);
    if (len < EPS) {
      result.push(q);
      continue;
    }
    const ux = dx / len;
    const uy = dy / len;
    const normal = upperNormal(dx, dy, len);
    const hops = markers.filter((m) => m.segmentIndex === i).map((m) => ({ m, t: (m.center.x - p.x) * ux + (m.center.y - p.y) * uy })).sort((h1, h2) => h1.t - h2.t);
    for (const { m, t } of hops) {
      const r = Math.min(m.radius, t, len - t);
      if (r <= EPS) continue;
      for (let k = 0; k <= segments; k++) {
        const theta = k * Math.PI / segments;
        const along = -Math.cos(theta) * r;
        const out = Math.sin(theta) * r;
        result.push({
          x: m.center.x + along * ux + out * normal.x,
          y: m.center.y + along * uy + out * normal.y
        });
      }
    }
    result.push(q);
  }
  return result;
}

// src/scene-order.ts
function splitTiers(nodes) {
  return {
    zones: nodes.filter((n) => n.type === "zone"),
    others: nodes.filter((n) => n.type !== "zone")
  };
}
function applyPerTier(nodes, ids, move) {
  const selected = new Set(ids);
  const { zones, others } = splitTiers(nodes);
  return [...move(zones, selected), ...move(others, selected)];
}
var toFront = (tier, selected) => {
  const kept = tier.filter((n) => !selected.has(n.id));
  const moved = tier.filter((n) => selected.has(n.id));
  return [...kept, ...moved];
};
var toBack = (tier, selected) => {
  const kept = tier.filter((n) => !selected.has(n.id));
  const moved = tier.filter((n) => selected.has(n.id));
  return [...moved, ...kept];
};
var forwardOne = (tier, selected) => {
  const result = [...tier];
  for (let i = result.length - 2; i >= 0; i--) {
    const current = result[i];
    const next = result[i + 1];
    if (selected.has(current.id) && !selected.has(next.id)) {
      result[i] = next;
      result[i + 1] = current;
    }
  }
  return result;
};
var backwardOne = (tier, selected) => {
  const result = [...tier];
  for (let i = 1; i < result.length; i++) {
    const current = result[i];
    const previous = result[i - 1];
    if (selected.has(current.id) && !selected.has(previous.id)) {
      result[i] = previous;
      result[i - 1] = current;
    }
  }
  return result;
};
function bringToFront(nodes, ids) {
  return applyPerTier(nodes, ids, toFront);
}
function sendToBack(nodes, ids) {
  return applyPerTier(nodes, ids, toBack);
}
function moveForward(nodes, ids) {
  return applyPerTier(nodes, ids, forwardOne);
}
function moveBackward(nodes, ids) {
  return applyPerTier(nodes, ids, backwardOne);
}

// src/scene-shapes.ts
var round2 = (value) => Math.round(value * 100) / 100;
function cloudPath(width, height) {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  const s = Math.min(w, h);
  const pts = [
    [0.16 * w, h],
    // Start unten links
    [0.08 * w, 0.62 * h],
    // linke Wulst endet
    [0.3 * w, 0.22 * h],
    // obere linke Wulst endet
    [0.62 * w, 0.18 * h],
    // obere mittlere Wulst endet
    [0.9 * w, 0.55 * h],
    // rechte Wulst endet
    [0.84 * w, h]
    // unten rechts
  ];
  const radii = [0.26 * s, 0.32 * s, 0.4 * s, 0.34 * s, 0.28 * s];
  const fmt = (value) => String(round2(value));
  let d = `M ${fmt(pts[0][0])} ${fmt(pts[0][1])}`;
  for (let i = 1; i < pts.length; i++) {
    const [x1, y1] = pts[i - 1];
    const [x2, y2] = pts[i];
    const chord = Math.hypot(x2 - x1, y2 - y1);
    const r = Math.max(radii[i - 1], chord / 2 + 0.5);
    d += ` A ${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(x2)} ${fmt(y2)}`;
  }
  d += ` L ${fmt(pts[0][0])} ${fmt(pts[0][1])} Z`;
  return d;
}

// src/scene-group.ts
var fallbackCounter = 0;
function defaultIdGen() {
  const cryptoApi = globalThis.crypto;
  return cryptoApi?.randomUUID?.() ?? `id-${Date.now()}-${fallbackCounter++}`;
}
function endpointRefs(endpoint, nodeIds) {
  return "nodeId" in endpoint && nodeIds.has(endpoint.nodeId);
}
function expandSelection(nodes, ids) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const groupIds = /* @__PURE__ */ new Set();
  for (const id of ids) {
    const groupId = byId.get(id)?.groupId;
    if (groupId) groupIds.add(groupId);
  }
  const result = new Set(ids);
  if (groupIds.size > 0) {
    for (const n of nodes) {
      if (n.groupId && groupIds.has(n.groupId)) result.add(n.id);
    }
  }
  return [...result];
}
function groupNodes(nodes, ids, idGen = defaultIdGen) {
  const selected = new Set(ids);
  const members = nodes.filter((n) => selected.has(n.id));
  if (members.length < 2) return [...nodes];
  const groupId = idGen();
  return nodes.map((n) => selected.has(n.id) ? { ...n, groupId } : n);
}
function ungroupNodes(nodes, ids) {
  const selected = new Set(ids);
  return nodes.map((n) => {
    if (!selected.has(n.id) || n.groupId === void 0) return n;
    const rest = { ...n };
    delete rest.groupId;
    return rest;
  });
}
function deleteSelection(scene, ids) {
  const selected = new Set(ids);
  const removedNodeIds = new Set(scene.nodes.filter((n) => selected.has(n.id)).map((n) => n.id));
  return {
    ...scene,
    nodes: scene.nodes.filter((n) => !selected.has(n.id)),
    connectors: scene.connectors.filter(
      (c) => !selected.has(c.id) && !endpointRefs(c.from, removedNodeIds) && !endpointRefs(c.to, removedNodeIds)
    )
  };
}
function duplicateSelection(scene, ids, idGen = defaultIdGen) {
  const selected = new Set(ids);
  const members = scene.nodes.filter((n) => selected.has(n.id));
  if (members.length === 0) return { scene, newIds: [] };
  const idMap = /* @__PURE__ */ new Map();
  const groupMap = /* @__PURE__ */ new Map();
  const clones = members.map((n) => {
    const cloneId = idGen();
    idMap.set(n.id, cloneId);
    const clone = { ...n, id: cloneId, x: n.x + 20, y: n.y + 20 };
    if (n.groupId) {
      if (!groupMap.has(n.groupId)) groupMap.set(n.groupId, idGen());
      clone.groupId = groupMap.get(n.groupId);
    }
    return clone;
  });
  const clonedConnectors = scene.connectors.filter(
    (c) => "nodeId" in c.from && "nodeId" in c.to && idMap.has(c.from.nodeId) && idMap.has(c.to.nodeId)
  ).map((c) => ({
    ...c,
    id: idGen(),
    from: { ...c.from, nodeId: idMap.get(c.from.nodeId) },
    to: { ...c.to, nodeId: idMap.get(c.to.nodeId) }
  }));
  return {
    scene: {
      ...scene,
      nodes: [...scene.nodes, ...clones],
      connectors: [...scene.connectors, ...clonedConnectors]
    },
    newIds: clones.map((c) => c.id)
  };
}
function selectionBounds(nodes, ids) {
  const selected = new Set(ids);
  const members = nodes.filter((n) => selected.has(n.id));
  if (members.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of members) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// src/scene-svg.ts
var DEFAULT_FILL = "#f1f5f9";
var DEFAULT_STROKE = "#64748b";
var TEXT_COLOR = "#0f172a";
function escapeXml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
function toBox(n) {
  return { x: n.x, y: n.y, width: n.width, height: n.height };
}
function sceneContentBounds(scene, padding = 24) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const include = (x, y) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const n of scene.nodes) {
    include(n.x, n.y);
    include(n.x + n.width, n.y + n.height);
  }
  for (const c of scene.connectors) {
    if ("x" in c.from) include(c.from.x, c.from.y);
    if ("x" in c.to) include(c.to.x, c.to.y);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, width: 200, height: 200 };
  return {
    minX: minX - padding,
    minY: minY - padding,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2
  };
}
function labelSvg(node, box) {
  if (!node.label) return "";
  const fontSize = node.fontSize ?? 14;
  const fill = node.textColor ?? TEXT_COLOR;
  const { align, verticalAlign } = labelAlignment(node);
  const bx = box?.x ?? node.x;
  const by = box?.y ?? node.y;
  const bw = box?.width ?? node.width;
  const bh = box?.height ?? node.height;
  const anchor = align === "left" ? "start" : align === "right" ? "end" : "middle";
  const x = align === "left" ? bx + 6 : align === "right" ? bx + bw - 6 : bx + bw / 2;
  const lines = node.label.split("\n");
  const lineHeight = fontSize * 1.2;
  const blockHeight = (lines.length - 1) * lineHeight;
  const startY = verticalAlign === "top" ? by + 6 + fontSize : verticalAlign === "bottom" ? by + bh - 6 - blockHeight : by + bh / 2 - blockHeight / 2 + fontSize / 3;
  const tspans = lines.map((line, i) => `<tspan x="${x}" y="${startY + i * lineHeight}">${escapeXml(line)}</tspan>`).join("");
  return `<text font-family="sans-serif" font-size="${fontSize}" fill="${fill}" text-anchor="${anchor}">${tspans}</text>`;
}
function deviceIconSvg(kind, s, color, ox, oy) {
  const sw = Math.max(1.5, s * 0.05);
  const S = `fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"`;
  const rect = (fx, fy, fw, fh, fr = 0) => `<rect x="${ox + fx * s}" y="${oy + fy * s}" width="${fw * s}" height="${fh * s}" rx="${fr * s}" ${S} />`;
  const line = (x1, y1, x2, y2, wMul = 1) => `<line x1="${ox + x1 * s}" y1="${oy + y1 * s}" x2="${ox + x2 * s}" y2="${oy + y2 * s}" fill="none" stroke="${color}" stroke-width="${sw * wMul}" stroke-linecap="round" stroke-linejoin="round" />`;
  const dot = (cx, cy, fr = 0.025) => `<circle cx="${ox + cx * s}" cy="${oy + cy * s}" r="${fr * s}" fill="${color}" />`;
  const ring = (cx, cy, fr) => `<circle cx="${ox + cx * s}" cy="${oy + cy * s}" r="${fr * s}" ${S} />`;
  switch (kind) {
    case "pc":
      return rect(0.12, 0.18, 0.76, 0.46, 0.04) + line(0.5, 0.64, 0.5, 0.78) + line(0.34, 0.8, 0.66, 0.8);
    case "laptop":
      return rect(0.22, 0.2, 0.56, 0.36, 0.03) + line(0.22, 0.56, 0.16, 0.66) + line(0.78, 0.56, 0.84, 0.66) + line(0.16, 0.66, 0.84, 0.66);
    case "printer":
      return rect(0.3, 0.22, 0.4, 0.16) + rect(0.16, 0.38, 0.68, 0.3, 0.03) + rect(0.32, 0.6, 0.36, 0.14) + dot(0.72, 0.46);
    case "scanner":
      return rect(0.16, 0.34, 0.68, 0.12, 0.02) + rect(0.16, 0.48, 0.68, 0.2, 0.02) + dot(0.74, 0.58);
    case "phone":
      return rect(0.34, 0.14, 0.32, 0.72, 0.07) + line(0.45, 0.22, 0.55, 0.22) + dot(0.5, 0.79);
    case "accesspoint":
      return rect(0.22, 0.5, 0.56, 0.18, 0.04) + dot(0.5, 0.59) + line(0.34, 0.5, 0.3, 0.34) + line(0.66, 0.5, 0.7, 0.34);
    case "camera":
      return rect(0.3, 0.24, 0.18, 0.08) + rect(0.16, 0.32, 0.68, 0.4, 0.04) + ring(0.5, 0.52, 0.12) + dot(0.5, 0.52, 0.04);
    case "server":
      return rect(0.26, 0.14, 0.48, 0.72, 0.03) + line(0.34, 0.3, 0.66, 0.3) + line(0.34, 0.46, 0.66, 0.46) + line(0.34, 0.62, 0.66, 0.62) + dot(0.34, 0.76) + dot(0.42, 0.76);
    case "pbx":
      return rect(0.18, 0.28, 0.64, 0.5, 0.03) + [0.42, 0.58].flatMap((cy) => [0.34, 0.5, 0.66].map((cx) => dot(cx, cy))).join("");
    case "deskphone":
      return rect(0.18, 0.5, 0.64, 0.28, 0.04) + line(0.24, 0.4, 0.76, 0.4, 1.4) + line(0.24, 0.4, 0.22, 0.47) + line(0.76, 0.4, 0.78, 0.47) + [0.4, 0.5, 0.6].map((cx) => dot(cx, 0.63)).join("");
    default:
      return rect(0.2, 0.2, 0.6, 0.6, 0.06) + `<text x="${ox + 0.5 * s}" y="${oy + 0.6 * s}" font-family="sans-serif" font-size="${0.4 * s}" font-weight="bold" fill="${color}" text-anchor="middle">?</text>`;
  }
}
function firewallIconSvg(role, s, color, ox, oy) {
  const sw = Math.max(1.5, s * 0.05);
  const S = `fill="none" stroke="${color}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"`;
  const rect = (fx, fy, fw, fh, fr = 0) => `<rect x="${ox + fx * s}" y="${oy + fy * s}" width="${fw * s}" height="${fh * s}" rx="${fr * s}" ${S} />`;
  const line = (x1, y1, x2, y2) => `<line x1="${ox + x1 * s}" y1="${oy + y1 * s}" x2="${ox + x2 * s}" y2="${oy + y2 * s}" ${S} />`;
  const poly = (pts) => {
    let p = "";
    for (let i = 0; i < pts.length; i += 2) p += `${ox + pts[i] * s},${oy + pts[i + 1] * s} `;
    return `<polygon points="${p.trim()}" ${S} />`;
  };
  switch (role) {
    case "router":
      return rect(0.3, 0.4, 0.4, 0.24, 0.04) + line(0.5, 0.4, 0.5, 0.16) + line(0.44, 0.24, 0.5, 0.16) + line(0.56, 0.24, 0.5, 0.16) + line(0.5, 0.64, 0.5, 0.86) + line(0.44, 0.78, 0.5, 0.86) + line(0.56, 0.78, 0.5, 0.86) + line(0.3, 0.52, 0.14, 0.52) + line(0.22, 0.46, 0.14, 0.52) + line(0.22, 0.58, 0.14, 0.52) + line(0.7, 0.52, 0.86, 0.52) + line(0.78, 0.46, 0.86, 0.52) + line(0.78, 0.58, 0.86, 0.52);
    case "utm":
      return poly([0.5, 0.16, 0.8, 0.28, 0.8, 0.54, 0.66, 0.74, 0.5, 0.86, 0.34, 0.74, 0.2, 0.54, 0.2, 0.28]) + line(0.4, 0.52, 0.47, 0.6) + line(0.47, 0.6, 0.62, 0.42);
    case "firewall":
    default:
      return rect(0.16, 0.22, 0.68, 0.6, 0.02) + line(0.16, 0.42, 0.84, 0.42) + line(0.16, 0.62, 0.84, 0.62) + line(0.39, 0.22, 0.39, 0.42) + line(0.61, 0.22, 0.61, 0.42) + line(0.28, 0.42, 0.28, 0.62) + line(0.5, 0.42, 0.5, 0.62) + line(0.72, 0.42, 0.72, 0.62) + line(0.39, 0.62, 0.39, 0.82) + line(0.61, 0.62, 0.61, 0.82);
  }
}
function firewallBodySvg(node) {
  const fw = node.firewall;
  if (!fw) return "";
  const rects = firewallInterfaceRects(toBox(node), fw.interfaces);
  const counts = firewallRuleCounts(fw);
  const iconSize = 22;
  const iconX = node.x + 6;
  const iconY = node.y + 6;
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="#334155" stroke-width="1.5" />`;
  const icon = firewallIconSvg(fw.role, iconSize, "#334155", iconX, iconY);
  const titleParts = [node.label || "Firewall", fw.role.toUpperCase()];
  if (fw.model) titleParts.push(fw.model);
  const titleSvg = `<text x="${iconX + iconSize + 6}" y="${node.y + 18}" font-family="sans-serif" font-size="11" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(titleParts.join("  \xB7  "))}</text>`;
  const ha = fw.haRole && fw.haRole !== "standalone" ? `<text x="${iconX + iconSize + 6}" y="${node.y + 30}" font-family="sans-serif" font-size="9" fill="#8b5cf6">HA ${escapeXml(fw.haRole)}</text>` : "";
  const ifaceSvg = rects.map((r) => {
    const iface = fw.interfaces[r.index - 1];
    const color = zoneColor(iface.zone);
    return `<rect x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" rx="2" fill="#f8fafc" stroke="${color}" stroke-width="1.5" /><text x="${r.x + r.width / 2}" y="${r.y + r.height / 2 + 3}" font-family="sans-serif" font-size="8" fill="${TEXT_COLOR}" text-anchor="middle">${escapeXml(iface.name)}</text>`;
  }).join("");
  const footerParts = [`${counts.rules} Regeln`];
  if (counts.nat > 0) footerParts.push(`${counts.nat} NAT`);
  footerParts.push(`${counts.vpns} VPNs`);
  const footer = `<text x="${node.x + 6}" y="${node.y + node.height - 4}" font-family="sans-serif" font-size="9" fill="#64748b">${escapeXml(footerParts.join(" \xB7 "))}</text>`;
  return housing + icon + titleSvg + ha + ifaceSvg + footer;
}
function firewallLegendBodySvg(node, firewallNodes) {
  const legend = node.firewallLegend ?? {};
  const sections = legend.sections ?? ["rules"];
  const showDisabled = legend.showDisabled ?? false;
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="#cbd5e1" stroke-width="1" />`;
  const source = legend.sourceNodeId ? firewallNodes.get(legend.sourceNodeId) : void 0;
  const title = `<text x="${node.x + 10}" y="${node.y + 16}" font-family="sans-serif" font-size="13" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(node.label || source?.label || "Firewall")}</text>`;
  if (!source || !source.firewall) {
    const hint = legend.sourceNodeId ? "Firewall nicht gefunden" : "Keine Firewall gewaehlt";
    return housing + title + `<text x="${node.x + 10}" y="${node.y + 38}" font-family="sans-serif" font-size="12" fill="#94a3b8">${hint}</text>`;
  }
  const fw = source.firewall;
  const x = node.x + 10;
  const lineH = 16;
  let y = node.y + 40;
  const text = (tx, ty, size, fill, bold, s) => `<text x="${tx}" y="${ty}" font-family="sans-serif" font-size="${size}" ${bold ? 'font-weight="bold" ' : ""}fill="${fill}">${escapeXml(s)}</text>`;
  let out = "";
  if (sections.includes("rules")) {
    out += text(x, y, 11, "#334155", true, "Regeln");
    y += lineH;
    for (const r of firewallLegendRules(fw, showDisabled)) {
      const dot = r.action === "allow" ? "#16a34a" : "#dc2626";
      const line = `${r.source || "any"} -> ${r.destination || "any"} : ${r.service || r.port || "any"}`;
      out += `<circle cx="${x + 4}" cy="${y - 4}" r="3" fill="${dot}" />` + text(x + 12, y, 10, TEXT_COLOR, false, line);
      y += lineH;
    }
  }
  if (sections.includes("nat")) {
    out += text(x, y, 11, "#334155", true, "NAT");
    y += lineH;
    for (const n of firewallLegendNat(fw, showDisabled)) {
      const line = `${n.name || ""} ${n.externalPort || ""} -> ${n.internalHost || ""}:${n.internalPort || ""}`;
      out += text(x + 12, y, 10, TEXT_COLOR, false, line.trim());
      y += lineH;
    }
  }
  if (sections.includes("vpns")) {
    out += text(x, y, 11, "#334155", true, "VPNs");
    y += lineH;
    for (const v of firewallLegendVpns(fw)) {
      const line = `${v.name} (${v.kind})${v.peer ? " " + v.peer : ""}`;
      out += text(x + 12, y, 10, TEXT_COLOR, false, line);
      y += lineH;
    }
  }
  return housing + title + out;
}
var IP_MODE_WORDS = { static: "statisch", dhcp: "DHCP", "dhcp-fixed": "DHCP-fest" };
function deviceBodySvg(node, vlans, users, options) {
  const device = node.device;
  const vlan = findVlan(vlans, device?.vlanRef);
  const iconColor = vlan?.color ?? "#475569";
  const frame = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="rgba(148,163,184,0.06)" stroke="#cbd5e1" stroke-width="1" />`;
  const iconSize = Math.max(16, Math.min(node.width, node.height * 0.45));
  const iconX = node.x + (node.width - iconSize) / 2;
  const iconY = node.y + 2;
  const href = device?.customImageId && options.imageHref ? options.imageHref(device.customImageId) : null;
  const visual = href ? `<image x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" href="${escapeXml(href)}" preserveAspectRatio="xMidYMid meet" />` : deviceIconSvg(device?.kind ?? "other", iconSize, iconColor, iconX, iconY);
  const lines = deviceInfoLines(node, vlans, users);
  const lineH = 13;
  const top = node.y + iconSize + 4;
  const cx = node.x + node.width / 2;
  const texts = lines.map((ln, i) => {
    let text;
    let fill = "#475569";
    let bold = false;
    if (ln.type === "name") {
      text = ln.text;
      fill = TEXT_COLOR;
      bold = true;
    } else if (ln.type === "vlan") {
      text = `VLAN: ${ln.vlanId}`;
      fill = ln.color;
    } else if (ln.type === "ip") {
      const word = IP_MODE_WORDS[ln.mode] ?? ln.mode;
      text = ln.mode === "dhcp" || !ln.value ? `IP: ${word}` : `IP: ${ln.value} (${word})`;
    } else if (ln.type === "dns") {
      text = `DNS: ${ln.value}`;
    } else if (ln.type === "user") {
      text = `Benutzer: ${ln.value}`;
    } else {
      text = ln.text;
    }
    const y = top + i * lineH + 9;
    return `<text x="${cx}" y="${y}" font-family="sans-serif" font-size="10"${bold ? ' font-weight="bold"' : ""} fill="${fill}" text-anchor="middle">${escapeXml(text)}</text>`;
  }).join("");
  return frame + visual + texts;
}
function switchBodySvg(node, vlans) {
  const sw = node.switch;
  if (!sw) return "";
  const rects = switchPortRects(toBox(node), sw);
  const byIndex = new Map(sw.ports.map((p) => [p.index, p]));
  const title = `${node.label || "Switch"}  \xB7  L${sw.layer} \xB7 ${sw.role}`;
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="#334155" stroke-width="1.5" />`;
  const titleSvg = `<text x="${node.x + 6}" y="${node.y + 13}" font-family="sans-serif" font-size="11" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(title)}</text>`;
  const ports = rects.map((r) => {
    const port = byIndex.get(r.index);
    const vlan = findVlan(vlans, port?.vlanRef);
    const occupied = !!(port?.deviceId || port?.vlanRef);
    const fill = vlan?.color ?? (occupied ? "#cbd5e1" : "#f1f5f9");
    const stroke = port?.uplink && sw.role === "core" ? "#b45309" : "#475569";
    const dash = r.sfp ? ' stroke-dasharray="3 2"' : "";
    const tagged = resolveTaggedVlans(port?.taggedVlanRefs, vlans);
    const maxDots = 4;
    const dots = tagged.slice(0, maxDots).map((v, i) => `<circle cx="${r.x + 4 + i * 5}" cy="${r.y + 3}" r="1.8" fill="${v.color}" />`).join("");
    const overflow = tagged.length > maxDots ? `<text x="${r.x + 4 + maxDots * 5}" y="${r.y + 5}" font-family="sans-serif" font-size="6" fill="${TEXT_COLOR}">+${tagged.length - maxDots}</text>` : "";
    const sfpMod = r.sfp && port?.sfpModule ? `<text x="${r.x + r.width / 2}" y="${r.y + r.height - 2}" font-family="sans-serif" font-size="6" fill="#475569" text-anchor="middle">${escapeXml(port.sfpModule)}</text>` : "";
    return `<rect x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" rx="${r.sfp ? 1 : 2}" fill="${fill}" stroke="${stroke}" stroke-width="1"${dash} /><text x="${r.x + r.width / 2}" y="${r.y + r.height / 2 + 3}" font-family="sans-serif" font-size="8" fill="${TEXT_COLOR}" text-anchor="middle">${r.index}</text>` + dots + overflow + sfpMod;
  }).join("");
  return housing + titleSvg + ports;
}
function switchLegendBodySvg(node, vlans, switchNodes) {
  const legend = node.switchLegend ?? {};
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="#cbd5e1" stroke-width="1" />`;
  const source = legend.sourceNodeId ? switchNodes.get(legend.sourceNodeId) : void 0;
  const title = `<text x="${node.x + 10}" y="${node.y + 16}" font-family="sans-serif" font-size="13" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(node.label || source?.label || "Switch-Ports")}</text>`;
  if (!source || !source.switch) {
    const hint = legend.sourceNodeId ? "Switch nicht gefunden" : "Kein Switch gewaehlt";
    return housing + title + `<text x="${node.x + 10}" y="${node.y + 38}" font-family="sans-serif" font-size="12" fill="#94a3b8">${hint}</text>`;
  }
  const rows = legendRows(source.switch, vlans, legend.showAllPorts ?? false);
  const colPort = node.x + 10;
  const colVlan = node.x + 70;
  const colTagged = node.x + 130;
  const colDose = node.x + node.width - 90;
  const labelColor = "#64748b";
  const header = `<text x="${colPort}" y="${node.y + 38}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${labelColor}">Port</text><text x="${colVlan}" y="${node.y + 38}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${labelColor}">VLAN</text><text x="${colTagged}" y="${node.y + 38}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${labelColor}">Tagged VLAN</text><text x="${colDose}" y="${node.y + 38}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${labelColor}">Dose</text><line x1="${node.x + 10}" y1="${node.y + 44}" x2="${node.x + node.width - 10}" y2="${node.y + 44}" stroke="#e2e8f0" stroke-width="1" />`;
  const rowH = 20;
  const rowsSvg = rows.map((row, i) => {
    const y = node.y + 60 + i * rowH;
    const portLabel = `${row.index}${row.portType === "sfp" ? " SFP" : ""}`;
    const portText = `<text x="${colPort}" y="${y}" font-family="sans-serif" font-size="11" fill="${TEXT_COLOR}">${escapeXml(portLabel)}</text>`;
    const vlanText = row.accessVlan ? `<circle cx="${colVlan + 4}" cy="${y - 4}" r="4" fill="${row.accessVlan.color}" /><text x="${colVlan + 12}" y="${y}" font-family="sans-serif" font-size="11" fill="${TEXT_COLOR}">${row.accessVlan.vlanId}</text>` : "";
    const maxTag = 4;
    const tagDots = row.taggedVlans.slice(0, maxTag).map((v, j) => `<circle cx="${colTagged + 4 + j * 10}" cy="${y - 4}" r="4" fill="${v.color}" />`).join("");
    const tagOverflow = row.taggedVlans.length > maxTag ? `<text x="${colTagged + 4 + maxTag * 10}" y="${y}" font-family="sans-serif" font-size="10" fill="${labelColor}">+${row.taggedVlans.length - maxTag}</text>` : "";
    const doseText = row.dose ? `<text x="${colDose}" y="${y}" font-family="sans-serif" font-size="11" fill="${TEXT_COLOR}">${escapeXml(row.dose)}</text>` : "";
    return portText + vlanText + tagDots + tagOverflow + doseText;
  }).join("");
  return housing + title + header + rowsSvg;
}
var REDUNDANCY_LABELS_DE = {
  none: "Keine",
  "mirror-2": "2-Wege-Spiegel",
  "mirror-3": "3-Wege-Spiegel",
  parity: "Parity",
  "parity-dual": "Dual-Parity"
};
function clusterStorageBodySvg(node, nodes) {
  const data = node.clusterStorage;
  if (!data) return "";
  const topOffset = clusterStorageContentTop(data);
  const rects = volumeTileRects(toBox(node), data.volumes.length, topOffset);
  const anyWarn = data.volumes.some((v) => volumeUsage(v).warn);
  const head = [node.label || "Clusterstorage", data.kind, data.cluster].filter(Boolean).join("  \xB7  ");
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="${anyWarn ? "#dc2626" : "#334155"}" stroke-width="${anyWarn ? 2 : 1.5}" />`;
  const header = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${CS_HEADER_H}" rx="8" fill="#f1f5f9" /><text x="${node.x + 8}" y="${node.y + 14}" font-family="sans-serif" font-size="11" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(head)}</text>` + (data.importedAt ? `<text x="${node.x + 8}" y="${node.y + 25}" font-family="sans-serif" font-size="9" fill="#64748b">Stand: ${escapeXml(data.importedAt)}</text>` : "");
  const warnVols = data.volumes.filter((v) => volumeUsage(v).warn);
  const warnSvg = warnVols.map((v, i) => `<text x="${node.x + 8}" y="${node.y + CS_HEADER_H + i * CS_WARN_LINE_H + 10}" font-family="sans-serif" font-size="10" font-weight="bold" fill="#dc2626">${escapeXml(v.name)}</text>`).join("");
  const tiles = rects.map((r) => {
    const vol = data.volumes[r.index];
    const usage = volumeUsage(vol);
    const meta = [vol.fs, vol.sizeGb != null ? `${vol.sizeGb} GB` : null, vol.redundancy ? REDUNDANCY_LABELS_DE[vol.redundancy] ?? vol.redundancy : null].filter(Boolean).join(" \xB7 ");
    const barW = Math.max(0, Math.min(1, usage.percent / 100)) * (r.width - 12);
    const vmN = volumeVmCount(nodes, vol.id);
    return `<rect x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" rx="4" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1" /><circle cx="${r.x + 8}" cy="${r.y + 8}" r="3.5" fill="${vol.color}" /><text x="${r.x + 16}" y="${r.y + 12}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(vol.name || "Volume")}</text>` + (meta ? `<text x="${r.x + 6}" y="${r.y + 26}" font-family="sans-serif" font-size="9" fill="#475569">${escapeXml(meta)}</text>` : "") + `<rect x="${r.x + 6}" y="${r.y + 32}" width="${r.width - 12}" height="8" rx="2" fill="#e2e8f0" /><rect x="${r.x + 6}" y="${r.y + 32}" width="${barW}" height="8" rx="2" fill="${usage.warn ? "#dc2626" : "#0d9488"}" /><text x="${r.x + 6}" y="${r.y + 49}" font-family="sans-serif" font-size="9" fill="${usage.warn ? "#dc2626" : "#64748b"}">${usage.percent}%</text><text x="${r.x + 6}" y="${r.y + r.height - 5}" font-family="sans-serif" font-size="8" fill="#94a3b8">${vmN} VMs</text>`;
  }).join("");
  return housing + header + warnSvg + tiles;
}
function hostBodySvg(node, vlans, nodes) {
  const host = node.host;
  if (!host) return "";
  const oc = hostOvercommit(host);
  const overcommit = oc.cpu || oc.ram;
  const topOffset = hostContentTop(host);
  const rects = hostTileRects(toBox(node), host.vms.length, topOffset);
  const head = [node.label || "Host", host.os, host.hypervisor, host.ip].filter(Boolean).join("  \xB7  ");
  const housing = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="8" fill="#ffffff" stroke="${overcommit ? "#dc2626" : "#334155"}" stroke-width="${overcommit ? 2 : 1.5}" />`;
  const header = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${HOST_HEADER_H}" rx="8" fill="#f1f5f9" /><text x="${node.x + 8}" y="${node.y + 15}" font-family="sans-serif" font-size="11" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(head)}</text>`;
  const warnings = [oc.cpu ? "CPU-Kerne \xFCberbelegt" : null, oc.ram ? "RAM \xFCberbelegt" : null].filter(
    Boolean
  );
  const warningSvg = warnings.map(
    (w, i) => `<text x="${node.x + 8}" y="${node.y + HOST_HEADER_H + i * WARN_LINE_H + 10}" font-family="sans-serif" font-size="10" font-weight="bold" fill="#dc2626">${escapeXml(w)}</text>`
  ).join("");
  const tiles = rects.map((r) => {
    const vm = host.vms[r.index];
    const vlan = findVlan(vlans, vm.vlanRef);
    const volume = findVolume(nodes, vm.volumeRef);
    const wide = r.width > 120;
    const fill = vm.active ? "#ecfdf5" : "#f1f5f9";
    const stroke = vm.active ? "#10b981" : "#cbd5e1";
    const hw = [vm.cores != null ? `${vm.cores}C` : null, vm.ram != null ? `${vm.ram} GB` : null].filter(Boolean).join(" \xB7 ");
    const osRow = r.height >= 40 && vm.os ? `<text x="${r.x + 6}" y="${r.y + 23}" font-family="sans-serif" font-size="9" fill="#475569">${escapeXml(vm.os)}</text>` : "";
    const hwRow = r.height >= 50 && hw ? `<text x="${r.x + 6}" y="${r.y + 33}" font-family="sans-serif" font-size="9" fill="#64748b">${escapeXml(hw)}</text>` : "";
    const vlanRow = r.height >= 60 && vlan ? wide ? `<text x="${r.x + 6}" y="${r.y + 44}" font-family="sans-serif" font-size="9" fill="#64748b">VLAN</text><circle cx="${r.x + 48}" cy="${r.y + 40}" r="3.5" fill="${vlan.color}" /><text x="${r.x + 56}" y="${r.y + 44}" font-family="sans-serif" font-size="9" fill="#475569">${escapeXml(String(vlan.vlanId))}</text>` : `<circle cx="${r.x + 8}" cy="${r.y + 40}" r="3.5" fill="${vlan.color}" />` : "";
    const storageRow = r.height >= 70 && volume ? wide ? `<text x="${r.x + 6}" y="${r.y + 55}" font-family="sans-serif" font-size="9" fill="#64748b">Clusterstorage</text><circle cx="${r.x + 84}" cy="${r.y + 51}" r="3.5" fill="${volume.color}" /><text x="${r.x + 92}" y="${r.y + 55}" font-family="sans-serif" font-size="9" fill="#475569">${escapeXml(volume.name)}</text>` : `<circle cx="${r.x + 8}" cy="${r.y + 51}" r="3.5" fill="${volume.color}" />` : "";
    return `<rect x="${r.x}" y="${r.y}" width="${r.width}" height="${r.height}" rx="4" fill="${fill}" stroke="${stroke}" stroke-width="1" /><text x="${r.x + 6}" y="${r.y + 12}" font-family="sans-serif" font-size="10" font-weight="bold" fill="${TEXT_COLOR}">${escapeXml(vm.name || "VM")}</text>` + osRow + hwRow + vlanRow + storageRow + `<text x="${r.x + 6}" y="${r.y + r.height - 5}" font-family="sans-serif" font-size="8" fill="${vm.active ? "#059669" : "#94a3b8"}">${vm.active ? "aktiv" : "inaktiv"}</text>`;
  }).join("");
  return housing + header + warningSvg + tiles;
}
function nodeSvg(node, options, vlans, users, switchNodes, firewallNodes, nodes) {
  const fill = node.fill ?? DEFAULT_FILL;
  const stroke = node.stroke ?? DEFAULT_STROKE;
  switch (node.type) {
    case "switch":
      return switchBodySvg(node, vlans);
    case "switch-legend":
      return switchLegendBodySvg(node, vlans, switchNodes);
    case "host":
      return hostBodySvg(node, vlans, nodes);
    case "clusterstorage":
      return clusterStorageBodySvg(node, nodes);
    case "firewall":
      return firewallBodySvg(node);
    case "firewall-legend":
      return firewallLegendBodySvg(node, firewallNodes);
    case "device":
      return deviceBodySvg(node, vlans, users, options);
    case "zone": {
      const dash = node.environmentType === "cloud" ? ' stroke-dasharray="10 6"' : "";
      const title = escapeXml(node.label || (node.environmentType === "cloud" ? "Cloud" : "Vor Ort"));
      return `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="14" fill="${fill}" fill-opacity="0.4" stroke="${stroke}" stroke-width="2"${dash} /><text x="${node.x + 12}" y="${node.y + 20}" font-family="sans-serif" font-size="14" font-weight="bold" fill="${stroke}">${title}</text>`;
    }
    case "circle": {
      const ir = inscribedRect(node.width, node.height);
      return `<ellipse cx="${node.x + node.width / 2}" cy="${node.y + node.height / 2}" rx="${node.width / 2}" ry="${node.height / 2}" fill="${fill}" stroke="${stroke}" stroke-width="2" />` + labelSvg(node, { x: node.x + ir.x, y: node.y + ir.y, width: ir.width, height: ir.height });
    }
    case "cloud":
      return `<path d="${cloudPath(node.width, node.height)}" transform="translate(${node.x},${node.y})" fill="${fill}" stroke="${stroke}" stroke-width="1" />` + labelSvg(node);
    case "image": {
      const href = node.imageId && options.imageHref ? options.imageHref(node.imageId) : null;
      const frame = `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" fill="#f1f5f9" stroke="${stroke}" stroke-dasharray="4 4" rx="6" />`;
      const img = href ? `<image x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" href="${escapeXml(href)}" preserveAspectRatio="xMidYMid meet" />` : "";
      return frame + img + labelSvg(node);
    }
    case "text":
      return `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="6" fill="${fill}" stroke="${stroke}" stroke-width="1" />` + labelSvg(node);
    default:
      return `<rect x="${node.x}" y="${node.y}" width="${node.width}" height="${node.height}" rx="6" fill="${fill}" stroke="${stroke}" stroke-width="2" />` + labelSvg(node);
  }
}
function resolveConnectorRoute(connector, nodeMap, shapeMap, switchMap) {
  const fromCenter = resolveEndpoint(connector.from, nodeMap);
  const toCenter = resolveEndpoint(connector.to, nodeMap);
  if (!fromCenter || !toCenter) return null;
  let from = fromCenter;
  let to = toCenter;
  if ("nodeId" in connector.from) {
    const box = nodeMap.get(connector.from.nodeId);
    const sw = switchMap.get(connector.from.nodeId);
    if (box && connector.from.portIndex !== void 0 && sw) {
      from = portAnchorPoint(box, sw, connector.from.portIndex);
    } else if (box) {
      from = edgeAnchor(box, toCenter, { port: connector.from.port, shape: shapeMap.get(connector.from.nodeId) });
    }
  }
  if ("nodeId" in connector.to) {
    const box = nodeMap.get(connector.to.nodeId);
    const sw = switchMap.get(connector.to.nodeId);
    if (box && connector.to.portIndex !== void 0 && sw) {
      to = portAnchorPoint(box, sw, connector.to.portIndex);
    } else if (box) {
      to = edgeAnchor(box, fromCenter, { port: connector.to.port, shape: shapeMap.get(connector.to.nodeId) });
    }
  }
  return (connector.routing ?? "straight") === "orthogonal" ? orthogonalRoute(from, to, connector.bendRatio ?? 0.5) : [from, to];
}
function connectorSvg(connector, route, markers) {
  const display = retractArrowEnds(applyBridges(route, markers), {
    start: connector.arrowStart ?? false,
    end: connector.arrowEnd ?? true,
    gap: ARROW_GAP
  });
  const from = display[0];
  const to = display[display.length - 1];
  const stroke = connector.cableType ? cableColor(connector.cableType, connector.fiberMode) : connector.stroke ?? "#475569";
  const strokeWidth = connector.strokeWidth ?? 2;
  const style = connectorLineStyle(connector);
  const dash = style === "dashed" ? ' stroke-dasharray="8 6"' : style === "dotted" ? ' stroke-dasharray="2 6"' : "";
  const markerStart = connector.arrowStart ?? false ? ' marker-start="url(#arrow-start)"' : "";
  const markerEnd = connector.arrowEnd ?? true ? ' marker-end="url(#arrow-end)"' : "";
  const points = display.map((p) => `${p.x},${p.y}`).join(" ");
  let line;
  if (display.length === 2) {
    line = `<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}" stroke="${stroke}" stroke-width="${strokeWidth}"${dash}${markerStart}${markerEnd} />`;
  } else {
    line = `<polyline points="${points}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="round"${dash}${markerStart}${markerEnd} />`;
  }
  let label = "";
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  if (connector.label) {
    label += `<text x="${midX}" y="${midY - 6}" font-family="sans-serif" font-size="12" fill="${TEXT_COLOR}" text-anchor="middle">${escapeXml(connector.label)}</text>`;
  }
  if (connector.labelBelow) {
    label += `<text x="${midX}" y="${midY + 16}" font-family="sans-serif" font-size="12" fill="${TEXT_COLOR}" text-anchor="middle">${escapeXml(connector.labelBelow)}</text>`;
  }
  return line + label;
}
function sceneToSvg(scene, options = {}) {
  const padding = options.padding ?? 24;
  const background = options.background ?? true;
  const bounds = sceneContentBounds(scene, padding);
  const nodeMap = new Map(scene.nodes.map((n) => [n.id, toBox(n)]));
  const shapeMap = new Map(
    scene.nodes.map((n) => [n.id, n.type === "circle" ? "ellipse" : "rect"])
  );
  const switchMap = new Map(
    scene.nodes.filter((n) => n.switch).map((n) => [n.id, n.switch])
  );
  const switchNodeMap = new Map(
    scene.nodes.filter((n) => n.type === "switch" && n.switch).map((n) => [n.id, n])
  );
  const firewallNodeMap = new Map(
    scene.nodes.filter((n) => n.type === "firewall" && n.firewall).map((n) => [n.id, n])
  );
  const defs = '<defs><marker id="arrow-end" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="context-stroke" /></marker><marker id="arrow-start" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M10,0 L0,5 L10,10 z" fill="context-stroke" /></marker></defs>';
  const bg = background ? `<rect x="${bounds.minX}" y="${bounds.minY}" width="${bounds.width}" height="${bounds.height}" fill="#ffffff" />` : "";
  const routeMap = /* @__PURE__ */ new Map();
  const resolved = [];
  for (const c of scene.connectors) {
    const route = resolveConnectorRoute(c, nodeMap, shapeMap, switchMap);
    if (!route) continue;
    routeMap.set(c.id, route);
    resolved.push({ id: c.id, points: route, bridge: c.bridge });
  }
  const markersByConn = /* @__PURE__ */ new Map();
  for (const m of computeLineBridges(resolved, { enabled: scene.lineBridges !== false })) {
    const arr = markersByConn.get(m.connectorId);
    if (arr) arr.push(m);
    else markersByConn.set(m.connectorId, [m]);
  }
  const renderConn = (c) => {
    const route = routeMap.get(c.id);
    return route ? connectorSvg(c, route, markersByConn.get(c.id) ?? []) : "";
  };
  const zones = scene.nodes.filter((n) => n.type === "zone");
  const others = scene.nodes.filter((n) => n.type !== "zone");
  const isPortConnector = (c) => "nodeId" in c.from && c.from.portIndex !== void 0 || "nodeId" in c.to && c.to.portIndex !== void 0;
  const body = [
    ...zones.map((n) => nodeSvg(n, options, scene.vlans, scene.users, switchNodeMap, firewallNodeMap, scene.nodes)),
    ...scene.connectors.filter((c) => !isPortConnector(c)).map(renderConn),
    ...others.map((n) => nodeSvg(n, options, scene.vlans, scene.users, switchNodeMap, firewallNodeMap, scene.nodes)),
    ...scene.connectors.filter(isPortConnector).map(renderConn)
  ].join("");
  return (
    // XML-Prolog mit expliziter UTF-8-Deklaration, damit Sonderzeichen/Umlaute (z.B. "·")
    // in jedem Viewer korrekt dekodiert werden (sonst Latin-1-Mojibake wie "Â·").
    `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(bounds.width)}" height="${Math.round(bounds.height)}" viewBox="${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}">` + defs + bg + body + "</svg>"
  );
}
function collectSceneImageIds(scene) {
  const ids = /* @__PURE__ */ new Set();
  for (const n of scene.nodes) {
    if (n.type === "image" && n.imageId) ids.add(n.imageId);
    if (n.device?.customImageId) ids.add(n.device.customImageId);
  }
  return [...ids];
}

// src/image-util.ts
var ALLOWED_IMAGE_FORMATS = ["jpeg", "jpg", "png", "webp", "gif"];
function isAllowedImageFormat(format) {
  return ALLOWED_IMAGE_FORMATS.includes(format.toLowerCase());
}
function fitWithin(width, height, max) {
  if (width <= max && height <= max) return { width, height };
  const scale = Math.min(max / width, max / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

// src/group-tree.ts
function buildGroupTree(groups) {
  const nodes = /* @__PURE__ */ new Map();
  for (const group of groups) {
    nodes.set(group.id, { group, children: [] });
  }
  const roots = [];
  const attached = /* @__PURE__ */ new Set();
  for (const group of groups) {
    const node = nodes.get(group.id);
    const parent = group.parentId ? nodes.get(group.parentId) : void 0;
    if (!parent || parent === node) {
      roots.push(node);
      attached.add(group.id);
    } else {
      parent.children.push(node);
    }
  }
  const reachable = /* @__PURE__ */ new Set();
  const visit = (node) => {
    if (reachable.has(node.group.id)) return;
    reachable.add(node.group.id);
    for (const child of node.children) visit(child);
  };
  for (const root of roots) visit(root);
  for (const group of groups) {
    if (reachable.has(group.id)) continue;
    const node = nodes.get(group.id);
    roots.push(node);
    visit(node);
  }
  const rootIds = new Set(roots.map((r) => r.group.id));
  for (const node of nodes.values()) {
    node.children = node.children.filter((c) => !rootIds.has(c.group.id));
  }
  return roots;
}
function collectDescendantIds(groups, id) {
  const childrenByParent = /* @__PURE__ */ new Map();
  for (const group of groups) {
    if (!group.parentId) continue;
    const list = childrenByParent.get(group.parentId) ?? [];
    list.push(group.id);
    childrenByParent.set(group.parentId, list);
  }
  const result = [];
  const visited = /* @__PURE__ */ new Set([id]);
  const queue = [...childrenByParent.get(id) ?? []];
  while (queue.length > 0) {
    const current = queue.shift();
    if (visited.has(current)) continue;
    visited.add(current);
    result.push(current);
    queue.push(...childrenByParent.get(current) ?? []);
  }
  return result;
}
function filterGroupsForSearch(groups, query) {
  const term = query.trim().toLowerCase();
  if (!term) return new Set(groups.map((g) => g.id));
  const byId = new Map(groups.map((g) => [g.id, g]));
  const visible = /* @__PURE__ */ new Set();
  for (const group of groups) {
    if (!group.name.toLowerCase().includes(term)) continue;
    let current = group;
    const path = /* @__PURE__ */ new Set();
    while (current && !path.has(current.id)) {
      path.add(current.id);
      visible.add(current.id);
      current = current.parentId ? byId.get(current.parentId) : void 0;
    }
  }
  return visible;
}

// src/user-groups.ts
import { z as z13 } from "zod";
var SHARE_SUBJECT_TYPES = ["drawing", "drawing_group"];
var SHARE_GRANTEE_TYPES = ["user", "user_group"];
var userGroupNameSchema = z13.string().trim().min(1, "Bitte einen Namen angeben.").max(120);
var createUserGroupSchema = z13.object({
  name: userGroupNameSchema,
  description: z13.string().trim().max(500).nullable().optional()
});
var updateUserGroupSchema = z13.object({
  name: userGroupNameSchema.optional(),
  description: z13.string().trim().max(500).nullable().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "Keine Aenderungen angegeben." });
var addGroupMemberSchema = z13.object({
  userId: z13.string().uuid(),
  role: z13.enum(USER_GROUP_ROLES).default("member")
});
var setMemberRoleSchema = z13.object({ role: z13.enum(USER_GROUP_ROLES) });
var listSharesQuerySchema = z13.object({
  subjectType: z13.enum(SHARE_SUBJECT_TYPES),
  subjectId: z13.string().uuid()
});
var addShareSchema = z13.object({
  subjectType: z13.enum(SHARE_SUBJECT_TYPES),
  subjectId: z13.string().uuid(),
  granteeType: z13.enum(SHARE_GRANTEE_TYPES),
  granteeId: z13.string().uuid(),
  permission: z13.enum(SHARE_PERMISSIONS)
});
var directoryQuerySchema = z13.object({
  q: z13.string().trim().max(120).optional(),
  type: z13.enum(["user", "user_group", "all"]).default("all")
});

// src/access.ts
function resolveDrawingAccess(params) {
  if (params.userId === params.ownerId) return "owner";
  let best = null;
  for (const s of params.shares) {
    if (s.permission === "edit") return "edit";
    if (s.permission === "view") best = "view";
  }
  return best;
}
function canEdit(access) {
  return access === "owner" || access === "edit";
}
function buildGrantHulls(grants, allGroups) {
  return grants.map((grant) => ({
    ...grant,
    hull: /* @__PURE__ */ new Set([grant.subjectId, ...collectDescendantIds(allGroups, grant.subjectId)])
  }));
}
function inheritedPermissionsForGroup(grants, groupId) {
  if (!groupId) return [];
  return grants.filter((g) => g.hull.has(groupId)).map((g) => ({ permission: g.permission }));
}

// src/trash-policy.ts
function canViewAllTrash(role) {
  return roleAtLeast(role, "admin");
}
function canRestoreDrawing(role, isOwner) {
  return isOwner || roleAtLeast(role, "admin");
}
function canPurgeDrawing(role) {
  return roleAtLeast(role, "admin");
}
var DAY_MS = 24 * 60 * 60 * 1e3;
function selectExpiredTrash(items, now, retentionDays) {
  if (retentionDays <= 0) return [];
  const cutoff = now.getTime() - retentionDays * DAY_MS;
  return items.filter((it) => new Date(it.deletedAt).getTime() <= cutoff).map((it) => it.id);
}

// src/lock-policy.ts
var LOCK_TTL_SECONDS = 120;
var LOCK_HEARTBEAT_SECONDS = 30;
function isLockActive(lock, now) {
  if (!lock) return false;
  const expires = lock.expiresAt instanceof Date ? lock.expiresAt : new Date(lock.expiresAt);
  return expires.getTime() > now.getTime();
}
function evaluateLockRequest(lock, userId, now) {
  if (!lock) return { granted: true };
  if (lock.lockedBy === userId) return { granted: true };
  if (!isLockActive(lock, now)) return { granted: true };
  return { granted: false, holder: lock.lockedBy };
}

// src/mfa-policy.ts
import { z as z14 } from "zod";
var MAX_MFA_ATTEMPTS = 5;
var MFA_RECOVERY_CODE_COUNT = 10;
function mfaLoginRequirement({
  totpEnabled,
  mfaRequired,
  globalRequired
}) {
  if (totpEnabled) return "totp";
  if (mfaRequired || globalRequired) return "setup_required";
  return "none";
}
function globalMfaForced({
  envForced,
  dbStored
}) {
  return envForced || dbStored;
}
function normalizeRecoveryCode(input) {
  return input.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}
var mfaCodeSchema = z14.string().trim().min(6, "Bitte einen g\xFCltigen Code eingeben.").max(32, "Bitte einen g\xFCltigen Code eingeben.");

// src/account-policy.ts
function checkUserDeletion(input) {
  if (input.actorId === input.targetId) return { ok: false, reason: "self" };
  if (!canManageUser(input.actorRole, input.targetRole)) return { ok: false, reason: "forbidden" };
  if (input.targetIsLastActiveSuperadmin) return { ok: false, reason: "last_active_superadmin" };
  if (!input.transferTargetOk) return { ok: false, reason: "invalid_transfer_target" };
  return { ok: true };
}

// src/csrf-retry.ts
var CSRF_ERROR_CODES = ["FST_CSRF_INVALID_TOKEN", "FST_CSRF_MISSING_SECRET"];
function shouldRetryCsrfError(status, code, method, alreadyRetried) {
  if (status !== 403 || method === "GET" || alreadyRetried) return false;
  return code !== void 0 && CSRF_ERROR_CODES.includes(code);
}
export {
  ALLOWED_IMAGE_FORMATS,
  ANCHOR_PORTS,
  ARROW_GAP,
  BRIDGE_MODES,
  CABLE_TYPES,
  CLUSTER_STORAGE_KINDS,
  CONNECTOR_ROUTINGS,
  CS_HEADER_H,
  CS_WARN_LINE_H,
  DEFAULT_DATETIME_FORMAT,
  DEFAULT_TIME_ZONE,
  DEVICE_FIELDS,
  DEVICE_KINDS,
  ENVIRONMENT_TYPES,
  FIBER_MODES,
  FIREWALL_ROLES,
  HA_ROLES,
  HOST_HEADER_H,
  IFACE_ZONES,
  IMPORT_SOURCES,
  IP_MODES,
  LABEL_POSITIONS,
  LINE_STYLES,
  LOCK_HEARTBEAT_SECONDS,
  LOCK_TTL_SECONDS,
  MAX_MFA_ATTEMPTS,
  MERGE_STRATEGIES,
  MFA_RECOVERY_CODE_COUNT,
  NODE_TYPES,
  PORT_COUNTS,
  PORT_KINDS,
  PORT_PROTOCOLS,
  PORT_SPEEDS,
  PRESENCE_COLORS,
  PRESENCE_MAX_SELECTION,
  ROLES,
  RULE_ACTIONS,
  RULE_PROTOCOLS,
  SFP_COUNTS,
  SFP_MODULES,
  SHARE_GRANTEE_TYPES,
  SHARE_PERMISSIONS,
  SHARE_SUBJECT_TYPES,
  SWITCH_LAYERS,
  SWITCH_ROLES,
  TEXT_ALIGNS,
  USER_GROUP_ROLES,
  VOLUME_COLORS,
  VOLUME_REDUNDANCY,
  VOLUME_WARN_DEFAULT,
  VPN_KINDS,
  VPN_PROTOCOLS,
  WARN_LINE_H,
  Y_COMMENTS_KEY,
  Y_CONNECTORS_KEY,
  Y_META_KEY,
  Y_NODES_KEY,
  Y_USERS_KEY,
  Y_VLANS_KEY,
  addGroupMemberSchema,
  addSeconds,
  addShareSchema,
  applyBridges,
  applySceneToYDoc,
  bringToFront,
  buildGrantHulls,
  buildGroupTree,
  buildPorts,
  cableColor,
  canAssignRole,
  canEdit,
  canManageGroupMembers,
  canManageUser,
  canPurgeDrawing,
  canRestoreDrawing,
  canViewAllTrash,
  canvasMetaSchema,
  changePasswordSchema,
  checkUserDeletion,
  cloudPath,
  clusterStorageContentTop,
  clusterStorageDataSchema,
  collectDescendantIds,
  collectSceneImageIds,
  commentAnchorKey,
  commentAnchorSchema,
  commentThreadPosition,
  computeLineBridges,
  computeSnap,
  connectorLineStyle,
  connectorSchema,
  createDrawingSchema,
  createGroupSchema,
  createUserGroupSchema,
  createUserSchema,
  deleteSelection,
  deriveCableType,
  deviceDataSchema,
  deviceFields,
  deviceInfoLines,
  directoryQuerySchema,
  drawingNameSchema,
  duplicateSelection,
  duplicateVlanIds,
  edgeAnchor,
  emailSchema,
  emptyScene,
  evaluateLockRequest,
  expandSelection,
  filterGroupsForSearch,
  findUser,
  findVlan,
  findVolume,
  firewallAnchorPoint,
  firewallDataSchema,
  firewallInterfaceRects,
  firewallInterfaceSchema,
  firewallLegendDataSchema,
  firewallLegendNat,
  firewallLegendRules,
  firewallLegendVpns,
  firewallRuleCounts,
  firewallRuleSchema,
  fitWithin,
  followedViewport,
  formatInZone,
  formatServicePort,
  globalMfaForced,
  groupCommentThreads,
  groupNameSchema,
  groupNodes,
  growingCanvasBounds,
  hostContentTop,
  hostDataSchema,
  hostOvercommit,
  hostTileLayout,
  hostTileRects,
  inheritedPermissionsForGroup,
  inscribedRect,
  interfaceAnchorIndex,
  isAllowedImageFormat,
  isExpired,
  isLockActive,
  isValidIpv4,
  isValidIpv4Cidr,
  isValidPortNumber,
  labelAlignment,
  layerFields,
  legendRows,
  listDrawingsQuerySchema,
  listSharesQuerySchema,
  loginSchema,
  mapUserRows,
  mapVmRows,
  mapVolumeRows,
  mergeUsers,
  mergeVms,
  mergeVolumes,
  mfaCodeSchema,
  mfaLoginRequirement,
  moveBackward,
  moveForward,
  natRuleSchema,
  nextVolumeColor,
  nodeCenter,
  normalizeRecoveryCode,
  nowUtc,
  orthogonalRoute,
  parseCsv,
  passwordSchema,
  portAnchorPoint,
  portDefSchema,
  presenceColor,
  presenceInitials,
  presenceStateSchema,
  presenceViewportSchema,
  recommendedClusterStorageSize,
  recommendedFirewallWidth,
  recommendedHostSize,
  recommendedSwitchWidth,
  remoteSelectionColors,
  resolveDrawingAccess,
  resolveEndpoint,
  resolveTaggedVlans,
  retractArrowEnds,
  roleAtLeast,
  sceneCommentSchema,
  sceneContentBounds,
  sceneDocSchema,
  sceneDocToYDoc,
  sceneEndpointSchema,
  sceneNodeSchema,
  sceneToSvg,
  sceneVolumes,
  segmentIntersection,
  selectExpiredTrash,
  selectionBounds,
  sendToBack,
  servicePortSchema,
  setMemberRoleSchema,
  shouldRetryCsrfError,
  snapToGrid,
  stagePosForCenter,
  supportsLabelPosition,
  switchDataSchema,
  switchLegendDataSchema,
  switchPortRects,
  toIsoUtc,
  ungroupNodes,
  updateDrawingSchema,
  updateGroupSchema,
  updateUserGroupSchema,
  updateUserSchema,
  userDefSchema,
  userGroupNameSchema,
  userLabel,
  viewportCenterWorld,
  vlanColor,
  vlanDefSchema,
  vmDefSchema,
  vmKey,
  volumeDefSchema,
  volumeTileLayout,
  volumeTileRects,
  volumeUsage,
  volumeVmCount,
  vpnDefSchema,
  yDocToSceneDoc,
  zoneColor
};
//# sourceMappingURL=index.js.map